"use client";

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import dynamic from 'next/dynamic';
import { useChatContext } from '@/contexts/ChatContext';
import { useLanguage } from '@/contexts/LanguageContext';
import AlbumMessage from './AlbumMessage';
import RichTextDisplay from './RichTextDisplay';
import { isRichTextPayload, getRichTextPlainContent } from '@/lib/richText';
import { FileConfig, MessageConfig, UIConfig } from '@/lib/config';
import { searchGifs, getFeaturedGifs, registerShare, KlipyGif } from '@/lib/klipy';
import {
    MessageDto,
    GroupMessage,
    GroupMemberDto,
    AtUsersInfoItem,
    MessageType,
    MessageStatus,
    SelectedFile,
    UserType,
    MessageReactions
} from '@/types';
import {
    formatTimestamp,
    formatDuration,
    formatFileSize,
    createUniqueId,
    getAvatarColor,
    getInitials,
    createImagePreview,
    getVideoMetadata,
    saveDraft,
    loadDraft,
    clearDraft,
    processMessageText,
    getRoomDisplayName,
    getLocale
} from '@/lib/utils';
import packageJson from '../../package.json';
import {
    Send, Paperclip, Image, Video, FileText, Smile, Mic, MapPin, X, Play, Pause, Trash2, Square, Reply, MoreVertical, Download, Check, Users, Phone, Mail, Plus, Search, ChevronDown
} from 'lucide-react';
import styles from './CenterPanel.module.css';

// Dynamically import EmojiPicker to avoid SSR issues
const EmojiPicker = dynamic(() => import('emoji-picker-react'), { ssr: false });

// Props interface
interface CenterPanelProps {
    showSearchIcon?: boolean;
    hideHeader?: boolean;
}

// Ref interface for parent to trigger actions
export interface CenterPanelRef {
    toggleSearch: () => void;
    showMembers: () => void;
}

// Search result interface
interface SearchResult {
    messageId: string;
    type: 'text' | 'image' | 'video' | 'file' | 'voice';
    snippet: string;
    timestamp: number;
    senderNickname: string;
}

// Quick reaction emojis for context menu
const REACTION_EMOJIS = [
    '❤️',  // Love
    '👍',  // Like
    '👎',  // Dislike
    '😂',  // Laugh
    '😮',  // Wow/Shock
    '😢',  // Sad
    '😡',  // Angry
    '🎉',  // Celebration
    '🙏',  // Thanks/Gratitude
    '🔥',  // Fire/Awesome
    '💯',  // Perfect
    '👀',  // Looking/Noted
    '🤔',  // Thinking
    '😎',  // Cool
    '‼️',  // Emphasis
    '❓',  // Question
];

const TYPING_DEBOUNCE_MS = 5000;
const TYPING_REFRESH_MS = 3000;
const DEFAULT_FILE_SIZE_LIMIT_MB = 300;

const getFileSizeLimitMB = (): number => {
    const configuredLimit = FileConfig.MAX_SIZE / (1024 * 1024);
    if (!Number.isFinite(configuredLimit) || configuredLimit <= 0) {
        return DEFAULT_FILE_SIZE_LIMIT_MB;
    }
    return Math.round(configuredLimit);
};

const getFileSizeLimitErrorMessage = (template: string): string => {
    return template.replace('{{limit}}', String(getFileSizeLimitMB()));
};

// Helper to detect and extract sticker URLs from text (supports single or multiple)
// Moved outside component for performance - pure function with no dependencies
const extractStickerUrls = (text: string): string[] | null => {
    if (!text) return null;

    const trimmedText = text.trim();

    // Check for [STICKERS:url] or [STICKERS:url1|url2|url3] format
    const stickerMatch = trimmedText.match(/^\[STICKERS:(.+)\]$/);
    if (stickerMatch) {
        const urls = stickerMatch[1].split('|').map(url => url.trim()).filter(url => url);
        return urls.length > 0 ? urls : null;
    }

    // Check if it's a direct GIF/image URL (Tenor or other sources)
    if (trimmedText.match(/^https?:\/\/(media\.tenor\.com\/|.*\.(gif|GIF|png|PNG|webp|WEBP)).*$/)) {
        return [trimmedText];
    }

    return null;
};

const getMessageTextContent = (msg: MessageDto): string => {
    if (msg.contentType === MessageType.AtTextMessage) {
        return msg.atTextElem?.text || msg.textElem?.content || '';
    }
    return msg.textElem?.content || '';
};

const getMessageTextForDisplay = (msg: MessageDto): string => {
    const text = getMessageTextContent(msg);
    if (!text || msg.contentType !== MessageType.AtTextMessage) {
        return text;
    }

    const atUsersInfo = msg.atTextElem?.atUsersInfo || [];
    if (atUsersInfo.length === 0) {
        return text;
    }

    let mentionIndex = 0;
    return text.replace(/(^|\s)(@[^\s]+)/g, (fullMatch, leadingSpace, token) => {
        if (mentionIndex >= atUsersInfo.length) return fullMatch;
        const mentionInfo = atUsersInfo[mentionIndex++];
        const nickname = mentionInfo.groupNickname?.trim();
        const rawUserId = mentionInfo.atUserID?.trim() || '';
        const displayValue = nickname || rawUserId;
        if (!displayValue) return fullMatch;

        const suffixMatch = token.match(/[.,!?;:]+$/);
        const suffix = suffixMatch ? suffixMatch[0] : '';
        const renderedMention = displayValue.includes('@') ? displayValue : `@${displayValue}`;
        return `${leadingSpace}${renderedMention}${suffix}`;
    });
};

const normalizeMentionValue = (value?: string): string => {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/^@+/, '').toLowerCase();
};

const addMentionCandidate = (set: Set<string>, value?: string) => {
    const normalized = normalizeMentionValue(value);
    if (!normalized) return;
    set.add(normalized);
    if (normalized.includes('@')) {
        set.add(normalized.split('@')[0]);
    }
};

const resolveMemberFromMentionInfo = (mentionInfo: AtUsersInfoItem, members: GroupMemberDto[]): GroupMemberDto | null => {
    const atUserID = normalizeMentionValue(mentionInfo.atUserID);
    const groupNickname = normalizeMentionValue(mentionInfo.groupNickname);

    for (const member of members) {
        const memberCandidates = new Set<string>();
        addMentionCandidate(memberCandidates, member.userID);
        addMentionCandidate(memberCandidates, member.email);
        addMentionCandidate(memberCandidates, member.nickname);

        if ((atUserID && memberCandidates.has(atUserID)) || (groupNickname && memberCandidates.has(groupNickname))) {
            return member;
        }
    }

    return null;
};

const buildMentionCandidates = (mentionInfo: AtUsersInfoItem, resolvedMember: GroupMemberDto | null): Set<string> => {
    const candidates = new Set<string>();
    addMentionCandidate(candidates, mentionInfo.atUserID);
    addMentionCandidate(candidates, mentionInfo.groupNickname);

    if (resolvedMember) {
        addMentionCandidate(candidates, resolvedMember.userID);
        addMentionCandidate(candidates, resolvedMember.email);
        addMentionCandidate(candidates, resolvedMember.nickname);
    }

    return candidates;
};

const renderAtTextWithClickableMentions = (
    text: string,
    atUsersInfo: AtUsersInfoItem[],
    members: GroupMemberDto[],
    onMentionClick: (member: GroupMemberDto) => void
): React.ReactNode => {
    if (!text) return null;
    if (atUsersInfo.length === 0) {
        return <span dangerouslySetInnerHTML={{ __html: processMessageText(text) }} />;
    }

    let mentionIndex = 0;
    const lines = text.split('\n');
    const nodes: React.ReactNode[] = [];

    lines.forEach((line, lineIndex) => {
        const parts = line.split(/(\s+)/).filter(Boolean);
        parts.forEach((part, partIndex) => {
            if (/^\s+$/.test(part)) {
                nodes.push(part);
                return;
            }

            const suffixMatch = part.match(/[.,!?;:]+$/);
            const suffix = suffixMatch ? suffixMatch[0] : '';
            const coreToken = suffix ? part.slice(0, -suffix.length) : part;
            const normalizedToken = normalizeMentionValue(coreToken);
            const mentionInfo = atUsersInfo[mentionIndex];

            if (!mentionInfo || !normalizedToken) {
                nodes.push(part);
                return;
            }

            const resolvedMember = resolveMemberFromMentionInfo(mentionInfo, members);
            const mentionCandidates = buildMentionCandidates(mentionInfo, resolvedMember);
            const isLikelyMentionToken = coreToken.startsWith('@') || coreToken.includes('@');
            const isMentionMatch = mentionCandidates.has(normalizedToken) || isLikelyMentionToken;

            if (!isMentionMatch) {
                nodes.push(part);
                return;
            }

            mentionIndex += 1;
            const fallbackMember: GroupMemberDto = {
                userID: mentionInfo.atUserID || normalizedToken,
                nickname: mentionInfo.groupNickname || coreToken.replace(/^@+/, ''),
                email: normalizedToken.includes('@') ? normalizedToken : undefined,
            };
            const mentionMember = resolvedMember || fallbackMember;

            nodes.push(
                <button
                    key={`mention-${lineIndex}-${partIndex}-${mentionIndex}`}
                    type="button"
                    className={`mention ${styles.mentionButton}`}
                    onClick={() => onMentionClick(mentionMember)}
                >
                    {coreToken}
                </button>
            );
            if (suffix) nodes.push(suffix);
        });

        if (lineIndex < lines.length - 1) {
            nodes.push(<br key={`mention-line-break-${lineIndex}`} />);
        }
    });

    return <>{nodes}</>;
};

// Helper function to get quoted message content preview
const getQuotedContentPreview = (quotedMessage: MessageDto, t: { replySticker: string; replyPhoto: string; replyVideo: string; file: string; replyVoiceMessage: string; replyLocation: string; reply: string; replyMessage: string }): { text: string; type: 'text' | 'image' | 'video' | 'file' | 'audio' | 'location' | 'sticker' } => {
    const contentType = quotedMessage.contentType;

    // Check for sticker (text message with sticker URL pattern)
    if (
        contentType === MessageType.TextMessage ||
        contentType === MessageType.AtTextMessage ||
        contentType === MessageType.AdvancedTextMessage
    ) {
        const content = getMessageTextForDisplay(quotedMessage);
        if (!content) {
            return { text: t.replyMessage, type: 'text' };
        }
        if (content.includes('media.tenor.com') || content.includes('tenor.com/view')) {
            return { text: `🎭 ${t.replySticker}`, type: 'sticker' };
        }
        return { text: getRichTextPlainContent(content), type: 'text' };
    }

    if (contentType === MessageType.PictureMessage) {
        return { text: `📷 ${t.replyPhoto}`, type: 'image' };
    }

    if (contentType === MessageType.VideoMessage) {
        return { text: `🎬 ${t.replyVideo}`, type: 'video' };
    }

    if (contentType === MessageType.FileMessage) {
        const fileName = quotedMessage.fileElem?.fileName || t.file;
        return { text: `📎 ${fileName}`, type: 'file' };
    }

    if (contentType === MessageType.VoiceMessage) {
        const duration = quotedMessage.soundElem?.duration || 0;
        return { text: `🎤 ${t.replyVoiceMessage} (${Math.ceil(duration)}s)`, type: 'audio' };
    }

    if (contentType === MessageType.LocationMessage) {
        return { text: `📍 ${t.replyLocation}`, type: 'location' };
    }

    if (contentType === MessageType.QuoteMessage && quotedMessage.quoteElem) {
        return { text: quotedMessage.quoteElem.text || t.reply, type: 'text' };
    }

    if (contentType === MessageType.FaceMessage) {
        return { text: `😀 ${t.replySticker}`, type: 'sticker' };
    }

    return { text: t.replyMessage, type: 'text' };
};

const getReactionCustomData = (msg: MessageDto): { emoji: string; messageId?: string; userId?: string } | null => {
    if (msg.contentType !== MessageType.CustomMessage || !msg.customElem?.data) return null;
    try {
        const customData = JSON.parse(msg.customElem.data);
        if (
            customData?.type === 'reaction' &&
            typeof customData.emoji === 'string' &&
            customData.emoji.trim()
        ) {
            return customData;
        }
    } catch {
        return null;
    }
    return null;
};

const hasDisplayableContent = (msg: MessageDto): boolean => {
    const isAlbum = msg.contentType === MessageType.TextMessage &&
        msg.textElem?.content?.startsWith('[ALBUM:');
    const hasStickerUrl = msg.contentType === MessageType.TextMessage &&
        msg.textElem?.content && extractStickerUrls(msg.textElem.content);
    const isReactionCustomMessage = !!getReactionCustomData(msg);

    return !!(
        isAlbum ||
        hasStickerUrl ||
        (
            (msg.contentType === MessageType.TextMessage ||
                msg.contentType === MessageType.AtTextMessage ||
                msg.contentType === MessageType.AdvancedTextMessage) &&
            getMessageTextContent(msg)
        ) ||
        (msg.contentType === MessageType.QuoteMessage && msg.quoteElem) ||
        (msg.contentType === MessageType.PictureMessage && msg.pictureElem) ||
        (msg.contentType === MessageType.VideoMessage && msg.videoElem) ||
        (msg.contentType === MessageType.FileMessage && msg.fileElem) ||
        (msg.contentType === MessageType.VoiceMessage && msg.soundElem) ||
        (msg.contentType === MessageType.LocationMessage && msg.locationElem) ||
        isReactionCustomMessage ||
        msg.contentType === MessageType.RevokeMessage
    );
};

const getMessageReactionSignature = (reactions?: Record<string, string[]>): string => {
    if (!reactions) return '';
    return Object.entries(reactions)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([emoji, userIds]) => `${emoji}:${[...userIds].sort().join(',')}`)
        .join('|');
};

const getMessageUpdateSignature = (message: MessageDto): string => {
    return JSON.stringify({
        status: message.status,
        serverMsgID: message.serverMsgID,
        seq: message.seq,
        isRead: message.isRead,
        readAt: message.readAt,
        contentType: message.contentType,
        ex: message.ex,
        localEx: message.localEx,
        sendError: message.sendError,
        attachedInfo: message.attachedInfo,
        textElem: message.textElem,
        atTextElem: message.atTextElem,
        pictureElem: message.pictureElem,
        videoElem: message.videoElem,
        fileElem: message.fileElem,
        soundElem: message.soundElem,
        locationElem: message.locationElem,
        quoteElem: message.quoteElem,
    });
};

const getMessageConversationKey = (message: MessageDto): string => {
    return message.groupID || message.recvID || '';
};

const getMessageIdentityKeys = (message: MessageDto): string[] => {
    const keys: string[] = [];

    if (message.clientMsgID && !message.clientMsgID.startsWith('failed_')) {
        keys.push(`client:${message.clientMsgID}`);
    }
    if (message.serverMsgID) {
        keys.push(`server:${message.serverMsgID}`);
    }

    const conversationKey = getMessageConversationKey(message);
    if (conversationKey && typeof message.seq === 'number' && message.seq > 0) {
        keys.push(`seq:${conversationKey}:${message.seq}`);
    }

    return keys;
};

const findMessageIndexByIdentity = (messages: MessageDto[], incoming: MessageDto): number => {
    const incomingKeys = new Set(getMessageIdentityKeys(incoming));
    if (incomingKeys.size === 0) {
        return -1;
    }

    return messages.findIndex(message =>
        getMessageIdentityKeys(message).some(key => incomingKeys.has(key))
    );
};

const upsertMessageByClientID = (messages: MessageDto[], incoming: MessageDto): MessageDto[] => {
    const index = findMessageIndexByIdentity(messages, incoming);
    if (index === -1) return [...messages, incoming];

    const existing = messages[index];
    if (
        existing.status === MessageStatus.Failed &&
        incoming.status !== MessageStatus.Succeeded &&
        !incoming.serverMsgID
    ) {
        return messages;
    }

    const merged: MessageDto = {
        ...existing,
        ...incoming,
        isRead: existing.isRead || incoming.isRead,
        readAt: existing.readAt || incoming.readAt,
        sendError: incoming.status === MessageStatus.Failed
            ? (incoming.sendError || existing.sendError)
            : incoming.sendError,
    };

    if (getMessageUpdateSignature(existing) === getMessageUpdateSignature(merged)) {
        return messages;
    }

    const next = [...messages];
    next[index] = merged;
    return next;
};

const dedupeMessagesByIdentity = (messages: MessageDto[]): MessageDto[] => {
    return messages.reduce(
        (next, message) => upsertMessageByClientID(next, message),
        [] as MessageDto[]
    );
};

const createLocalFailedMessage = (params: {
    currentUserID: string;
    groupID: string;
    requestId: string;
    content: string;
    sendError: string;
}): MessageDto => {
    const now = Date.now();

    return {
        clientMsgID: `failed_${createUniqueId()}`,
        createTime: now,
        sendTime: now,
        sessionType: 3,
        sendID: params.currentUserID,
        recvID: '',
        groupID: params.groupID,
        contentType: MessageType.TextMessage,
        status: MessageStatus.Failed,
        ex: JSON.stringify({ requestId: params.requestId }),
        sendError: params.sendError,
        textElem: {
            content: params.content,
        },
    };
};

interface MessageItemProps {
    msg: MessageDto;
    members: GroupMemberDto[];
    isLastInGroup: boolean;
    isOutgoing: boolean;
    currentUserID: string;
    groupReadAt?: number;
    readTimestamp?: number;
    reactions?: Record<string, string[]>;
    reactionSignature: string;
    groupID: string;
    onImageClick: (msg: MessageDto) => void;
    openAlbumViewer: (images: string[], currentIndex: number) => void;
    openVideoViewer: (msg: MessageDto) => void;
    downloadFile: (msg: MessageDto) => void;
    openLocation: (lat: number, lng: number) => void;
    sendReaction: (params: { emoji: string; messageId: string; groupID: string }) => Promise<boolean>;
    onMentionClick: (member: GroupMemberDto) => void;
}

interface MessageStatusMetaProps {
    msg: MessageDto;
    isOutgoing: boolean;
    groupReadAt?: number;
    readTimestamp?: number;
}

const getAlbumStatusMessage = (albumImages: MessageDto[]): MessageDto => {
    const reversed = [...albumImages].reverse();
    return (
        reversed.find(message => message.status === MessageStatus.Failed) ||
        reversed.find(message => message.status === MessageStatus.Sending) ||
        albumImages[albumImages.length - 1]
    );
};

const getLatestReadAt = (messages: MessageDto[]): number | undefined => {
    return messages.reduce<number | undefined>((latest, message) => {
        if (!message.readAt) return latest;
        if (!latest || message.readAt > latest) return message.readAt;
        return latest;
    }, undefined);
};

const MessageStatusMeta = React.memo<MessageStatusMetaProps>(function MessageStatusMeta({
    msg,
    isOutgoing,
    groupReadAt,
    readTimestamp
}) {
    const { t, language } = useLanguage();
    const failedStatusText = msg.sendError || t.messageSendFailed;

    return (
        <div className={`${styles.albumMeta} ${isOutgoing ? styles.outgoing : styles.incoming}`}>
            <span className={styles.messageTime}>{formatTimestamp(msg.sendTime, t, language)}</span>
            {isOutgoing && (
                <div className={styles.readStatus}>
                    {msg.status === MessageStatus.Sending ? (
                        <>
                            <div className={styles.sendingSpinner}></div>
                            <span className={styles.statusText}>{t.sending}</span>
                        </>
                    ) : msg.status === MessageStatus.Failed ? (
                        <>
                            <span className={styles.failedIcon}>!</span>
                            <span
                                className={`${styles.statusText} ${styles.failedText} ${styles.failedStatusText}`}
                                title={failedStatusText}
                            >
                                {failedStatusText ? `${t.failed}: ${failedStatusText}` : t.failed}
                            </span>
                        </>
                    ) : (msg.readAt || groupReadAt) ? (
                        <>
                            <Check size={14} className={styles.readReceipt} />
                            <span className={styles.statusText}>
                                {readTimestamp
                                    ? `${t.readOn} ${formatTimestamp(readTimestamp, t, language)}`
                                    : t.read}
                            </span>
                        </>
                    ) : msg.serverMsgID ? (
                        <>
                            <Check size={14} className={styles.deliveredReceipt} />
                            <span className={styles.statusText}>{t.delivered}</span>
                        </>
                    ) : (
                        <>
                            <Check size={14} className={styles.sentReceipt} />
                            <span className={styles.statusText}>{t.sent}</span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

const MessageItem = React.memo<MessageItemProps>(function MessageItem({
    msg,
    members,
    isLastInGroup,
    isOutgoing,
    currentUserID,
    groupReadAt,
    readTimestamp,
    reactions,
    reactionSignature,
    groupID,
    onImageClick,
    openAlbumViewer,
    openVideoViewer,
    downloadFile,
    openLocation,
    sendReaction,
    onMentionClick
}) {
    const { t, language } = useLanguage();
    const textContent = getMessageTextContent(msg);
    const displayTextContent = getMessageTextForDisplay(msg);
    const isAlbumMessage = msg.contentType === MessageType.TextMessage &&
        msg.textElem?.content &&
        msg.textElem.content.startsWith('[ALBUM:');

    const stickerUrls = !isAlbumMessage && msg.contentType === MessageType.TextMessage && msg.textElem
        ? extractStickerUrls(msg.textElem.content)
        : null;

    const reactionCustomData = getReactionCustomData(msg);

    return (
        <>
            {isAlbumMessage && msg.textElem && (
                <AlbumMessage
                    albumText={msg.textElem.content}
                    onOpenViewer={openAlbumViewer}
                />
            )}

            {!isAlbumMessage && (
                msg.contentType === MessageType.TextMessage ||
                msg.contentType === MessageType.AtTextMessage ||
                msg.contentType === MessageType.AdvancedTextMessage
            ) && textContent && (
                stickerUrls ? (
                    <div className={styles.stickerContainer}>
                        {stickerUrls.map((url, idx) => (
                            <img
                                key={idx}
                                src={url}
                                alt="Sticker"
                                className={styles.stickerImage}
                            />
                        ))}
                    </div>
                ) : (
                    <div className={styles.messageText}>
                        {msg.contentType === MessageType.AtTextMessage
                            ? renderAtTextWithClickableMentions(
                                displayTextContent,
                                msg.atTextElem?.atUsersInfo || [],
                                members,
                                onMentionClick
                            )
                            : isRichTextPayload(displayTextContent)
                                ? <RichTextDisplay payload={displayTextContent} />
                                : <span dangerouslySetInnerHTML={{ __html: processMessageText(displayTextContent) }} />
                        }
                    </div>
                )
            )}

            {msg.contentType === MessageType.CustomMessage && reactionCustomData && (
                <div className={styles.messageText}>
                    <span>{isOutgoing ? 'You reacted' : 'Reacted'} {reactionCustomData.emoji}</span>
                </div>
            )}

            {msg.contentType === MessageType.QuoteMessage && msg.quoteElem && (() => {
                const quotedMsg = msg.quoteElem.quoteMessage;
                const preview = getQuotedContentPreview(quotedMsg, t);
                const hasMediaPreview = preview.type === 'image' || preview.type === 'video' || preview.type === 'sticker';

                let thumbnailUrl: string | undefined;
                if (preview.type === 'image' && quotedMsg.pictureElem) {
                    thumbnailUrl = quotedMsg.pictureElem.snapshotPicture?.url || quotedMsg.pictureElem.sourcePicture?.url;
                } else if (preview.type === 'video' && quotedMsg.videoElem) {
                    thumbnailUrl = quotedMsg.videoElem.snapshotUrl;
                } else if (preview.type === 'sticker') {
                    const quotedText = getMessageTextContent(quotedMsg);
                    const urlMatch = quotedText.match(/https?:\/\/[^\s]+/);
                    if (urlMatch) thumbnailUrl = urlMatch[0];
                }

                return (
                    <div className={styles.messageText}>
                        <div
                            className={`${styles.quotedMessage} ${hasMediaPreview ? styles.quotedWithMedia : ''}`}
                            onClick={() => {
                                const el = document.querySelector(`[data-message-id="${quotedMsg.clientMsgID}"]`);
                                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                        >
                            <div className={styles.quotedContent}>
                                <span className={styles.quotedSender}>{quotedMsg.senderNickname}</span>
                                <span className={styles.quotedText}>{preview.text}</span>
                            </div>
                            {thumbnailUrl && (
                                <div className={styles.quotedThumbnail}>
                                    <img src={thumbnailUrl} alt="" />
                                    {preview.type === 'video' && (
                                        <div className={styles.quotedVideoIcon}>
                                            <Play size={12} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {isRichTextPayload(msg.quoteElem.text)
                            ? <RichTextDisplay payload={msg.quoteElem.text} />
                            : <span dangerouslySetInnerHTML={{ __html: processMessageText(msg.quoteElem.text) }} />
                        }
                    </div>
                );
            })()}

            {msg.contentType === MessageType.PictureMessage && msg.pictureElem && (
                <div className={styles.attachmentImageContainer} onClick={() => onImageClick(msg)}>
                    <img
                        src={msg.pictureElem.snapshotPicture?.url || msg.pictureElem.sourcePicture.url}
                        alt="Image"
                        className={styles.attachmentImage}
                    />
                    <div className={styles.imageOverlay}>
                        <button className={styles.attachmentActionBtn} onClick={(e) => { e.stopPropagation(); downloadFile(msg); }}>
                            <Download size={14} />
                        </button>
                    </div>
                </div>
            )}

            {msg.contentType === MessageType.VideoMessage && msg.videoElem && (
                <div className={styles.attachmentVideoContainer} onClick={() => openVideoViewer(msg)}>
                    <img
                        src={msg.videoElem.snapshotUrl}
                        alt="Video thumbnail"
                        className={styles.attachmentVideo}
                    />
                    <div className={styles.videoPlayOverlay}>
                        <Play size={56} className={styles.playIcon} />
                    </div>
                    <div className={styles.videoDuration}>
                        {formatDuration(msg.videoElem.duration || 0)}
                    </div>
                </div>
            )}

            {msg.contentType === MessageType.FileMessage && msg.fileElem && (
                <div className={styles.attachmentDocument} onClick={() => downloadFile(msg)}>
                    <div className={styles.documentInfo}>
                        <FileText size={24} />
                        <div className={styles.documentDetails}>
                            <span className={styles.documentName}>{msg.fileElem.fileName}</span>
                            <span className={styles.attachmentSize}>{formatFileSize(msg.fileElem.fileSize || 0)}</span>
                        </div>
                    </div>
                    <button className={styles.attachmentActionBtn}>
                        <Download size={14} />
                    </button>
                </div>
            )}

            {msg.contentType === MessageType.VoiceMessage && msg.soundElem && (
                <div className={styles.attachmentSound}>
                    <div className={styles.soundIcon}>
                        <Mic size={20} />
                    </div>
                    <div className={styles.soundInfo}>
                        <audio controls className={styles.soundPlayer} src={msg.soundElem.sourceUrl} />
                        <span className={styles.soundDuration}>{formatDuration(msg.soundElem.duration)}</span>
                    </div>
                </div>
            )}

            {msg.contentType === MessageType.LocationMessage && msg.locationElem && (
                <div className={styles.locationCard}>
                    <div className={styles.locationHeader}>
                        <MapPin size={18} className={styles.locationHeaderIcon} />
                        <span className={styles.locationLabel}>{msg.locationElem.description}</span>
                    </div>
                    <div className={styles.mapContainer}>
                        <iframe
                            width="100%"
                            height="200"
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${msg.locationElem.longitude - 0.01},${msg.locationElem.latitude - 0.01},${msg.locationElem.longitude + 0.01},${msg.locationElem.latitude + 0.01}&layer=mapnik&marker=${msg.locationElem.latitude},${msg.locationElem.longitude}`}
                            className={styles.mapIframe}
                        ></iframe>
                    </div>
                    <button
                        className={styles.openMapButton}
                        onClick={() => openLocation(msg.locationElem!.latitude, msg.locationElem!.longitude)}
                    >
                        <MapPin size={16} />
                        {t.openInMaps}
                    </button>
                </div>
            )}

            {msg.contentType === MessageType.RevokeMessage && (
                <div className={styles.revokedMessage}>
                    {t.thisMessageWasDeleted}
                </div>
            )}

            {reactions && Object.keys(reactions).length > 0 && (
                <div className={styles.messageReactions}>
                    {Object.entries(reactions).map(([emoji, userIds]) => (
                        <button
                            key={emoji}
                            className={`${styles.reactionBadge} ${userIds.includes(currentUserID) ? styles.reactionBadgeActive : ''}`}
                            onClick={() => sendReaction({ emoji, messageId: msg.clientMsgID, groupID })}
                            title={userIds.join(', ')}
                        >
                            <span className={styles.reactionEmoji}>{emoji}</span>
                            <span className={styles.reactionCount}>{userIds.length}</span>
                        </button>
                    ))}
                </div>
            )}

            {isLastInGroup && (
                <MessageStatusMeta
                    msg={msg}
                    isOutgoing={isOutgoing}
                    groupReadAt={groupReadAt}
                    readTimestamp={readTimestamp}
                />
            )}
        </>
    );
}, (prev, next) => (
    prev.msg === next.msg &&
    prev.isLastInGroup === next.isLastInGroup &&
    prev.isOutgoing === next.isOutgoing &&
    prev.currentUserID === next.currentUserID &&
    prev.groupID === next.groupID &&
    prev.groupReadAt === next.groupReadAt &&
    prev.readTimestamp === next.readTimestamp &&
    prev.reactionSignature === next.reactionSignature
));

const CenterPanel = forwardRef<CenterPanelRef, CenterPanelProps>(function CenterPanel({ showSearchIcon = false, hideHeader = false }, ref) {
    const { t, language } = useLanguage();
    const {
        selectedRoom,
        currentUserID,
        userType,
        loadMessages,
        sendTextMessage,
        sendImageMessage,
        sendVideoMessage,
        sendFileMessage,
        sendSoundMessage,
        sendLocationMessage,
        sendReplyMessage,
        sendAlbumMessage,
        revokeMessage,
        deleteMessageForMe,
        sendReaction,
        messageReactions,
        sendTypingStatus,
        typingStatus,
        openGroup,
        openImageViewer,
        openVideoViewer,
        searchMessages,
        messages: contextMessages
    } = useChatContext();

    // State
    const [messages, setMessages] = useState<MessageDto[]>([]);
    const [messageText, setMessageText] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    const [fileUploadError, setFileUploadError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
    const [showUserSuggestions, setShowUserSuggestions] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState<GroupMemberDto[]>([]);
    const [replyingTo, setReplyingTo] = useState<MessageDto | null>(null);

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [recordedAudio, setRecordedAudio] = useState<{ file: File; duration: number } | null>(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);

    // Location state
    const [selectedLocation, setSelectedLocation] = useState<{ latitude: number; longitude: number; description: string } | null>(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);

    // Album sending state
    const [isSendingAlbum, setIsSendingAlbum] = useState(false);
    const [albumProgress, setAlbumProgress] = useState<{ current: number; total: number } | null>(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; message: GroupMessage } | null>(null);

    // Members popup state
    const [showMembersPopup, setShowMembersPopup] = useState(false);
    const [selectedMember, setSelectedMember] = useState<GroupMemberDto | null>(null);
    const handleMentionClick = useCallback((member: GroupMemberDto) => {
        setSelectedMember(member);
    }, []);

    // GIF picker state
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [gifSearchQuery, setGifSearchQuery] = useState('');
    const [gifs, setGifs] = useState<KlipyGif[]>([]);
    const [gifLoading, setGifLoading] = useState(false);
    const [selectedStickers, setSelectedStickers] = useState<KlipyGif[]>([]);

    // Reaction picker state
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const reactionPickerRef = useRef<HTMLDivElement>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Expose actions to parent via ref
    useImperativeHandle(ref, () => ({
        toggleSearch: () => setIsSearchExpanded(prev => !prev),
        showMembers: () => setShowMembersPopup(true),
    }));

    // Search state
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const searchRequestSeqRef = useRef(0);

    // Refs
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const audioPreviewRef = useRef<HTMLAudioElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const draftTimerRef = useRef<NodeJS.Timeout | null>(null);
    const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const typingRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const typingRefreshConversationRef = useRef<string | null>(null);
    const activeTypingConversationRef = useRef<string | null>(null);
    const hasSentTypingStartRef = useRef(false);
    const oldestMessageIDRef = useRef<string | null>(null);
    const noMoreMessagesRef = useRef(false);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const attachmentMenuRef = useRef<HTMLDivElement>(null);
    const gifPickerRef = useRef<HTMLDivElement>(null);
    const previousIsOtherTypingRef = useRef(false);
    const isNearBottomRef = useRef(true); // Track if user is near bottom for auto-scroll
    const pendingScrollRef = useRef<'bottom' | 'restore' | 'preserve' | null>(null); // Track pending scroll action
    const scrollRestoreDataRef = useRef<{ prevHeight: number; scrollTop?: number } | null>(null); // Data for scroll restoration
    const ignoreScrollUntilRef = useRef<number>(0); // Timestamp until which scroll events should be ignored
    const lastKnownScrollTopRef = useRef<number>(0); // Always track scroll position for restoration
    const isInitialLoadRef = useRef(true); // Track if this is the initial load for the current conversation
    const initialMessageCountRef = useRef<number>(0); // Track the initial message count
    const [unreadCount, setUnreadCount] = useState(0); // Count of new messages while scrolled up
    const [showNewMessageButton, setShowNewMessageButton] = useState(false); // Show floating button

    // Helper function to get the real message ID (handles album messages)
    const getRealMessageID = (msg: MessageDto): string => {
        // Check if this is an album message
        if (msg.clientMsgID?.startsWith('album_') && msg.ex) {
            try {
                const exData = JSON.parse(msg.ex);
                // Return the first image's message ID from the album
                if (exData.imageMessageIDs && exData.imageMessageIDs.length > 0) {
                    return exData.imageMessageIDs[0];
                }
            } catch (e) {
                console.debug('Error parsing album ex data:', e);
            }
        }
        // Return the regular message ID
        return msg.clientMsgID;
    };

    // Track processed message IDs to avoid duplicate processing
    const processedMessageIdsRef = useRef<Set<string>>(new Set());

    // Store functions in refs to avoid re-running effect when they change
    const loadMessagesRef = useRef(loadMessages);
    useEffect(() => {
        loadMessagesRef.current = loadMessages;
    }, [loadMessages]);

    // Load messages when room changes - ONLY depends on conversationID
    useEffect(() => {
        if (!selectedRoom?.conversationID) {
            setMessages([]);
            processedMessageIdsRef.current.clear();
            return;
        }

        const loadInitial = async () => {
            setIsLoading(true);
            noMoreMessagesRef.current = false;
            oldestMessageIDRef.current = null;
            processedMessageIdsRef.current.clear(); // Clear when loading new room
            isNearBottomRef.current = true; // Reset for new conversation
            setUnreadCount(0); // Reset unread count for new conversation
            setShowNewMessageButton(false); // Hide new message button

            const conversationID = selectedRoom.conversationID || '';
            const msgs = await loadMessagesRef.current(conversationID);

            const initialMessages = dedupeMessagesByIdentity(msgs);

            // Add loaded messages to processed set
            initialMessages.forEach(m => processedMessageIdsRef.current.add(m.clientMsgID));

            // Get the real message ID (handles album messages)
            oldestMessageIDRef.current = initialMessages[0] ? getRealMessageID(initialMessages[0]) : null;

            // Set up scroll to bottom after messages load
            isNearBottomRef.current = true;
            pendingScrollRef.current = 'bottom';

            setMessages(initialMessages);
            setIsLoading(false);

            // Scroll to bottom with multiple attempts to handle async image loading
            // Ignore scroll events for 3 seconds during initial load
            ignoreScrollUntilRef.current = Date.now() + 3000;
            const scrollToBottom = () => {
                const container = messagesContainerRef.current;
                // Only scroll if user hasn't manually scrolled up
                if (container && isNearBottomRef.current) {
                    container.scrollTop = container.scrollHeight;
                }
            };

            // Immediate scroll after state update
            requestAnimationFrame(() => {
                requestAnimationFrame(scrollToBottom);
            });

            // Additional scroll attempts for images that may load later
            setTimeout(scrollToBottom, 100);
            setTimeout(scrollToBottom, 300);
            setTimeout(scrollToBottom, 500);

            // Load draft
            const draft = loadDraft(conversationID);
            if (draft) setMessageText(draft);
        };

        loadInitial();
    }, [selectedRoom?.conversationID]); // ONLY re-run when conversationID changes

    // Cache for parsed ex field to avoid repeated JSON.parse
    const parsedExCache = useRef<Map<string, any>>(new Map());

    // Helper to get parsed ex field with caching
    const getParsedEx = useCallback((msg: MessageDto): any => {
        if (!msg.ex) return {};
        const cacheKey = msg.clientMsgID;
        if (parsedExCache.current.has(cacheKey)) {
            return parsedExCache.current.get(cacheKey);
        }
        try {
            const parsed = JSON.parse(msg.ex);
            parsedExCache.current.set(cacheKey, parsed);
            return parsed;
        } catch {
            parsedExCache.current.set(cacheKey, {});
            return {};
        }
    }, []);

    // Helper functions - memoized
    const canGroupMessages = useCallback((prev: MessageDto, current: MessageDto): boolean => {
        if (prev.sendID !== current.sendID) return false;

        const prevEx = getParsedEx(prev);
        const currEx = getParsedEx(current);
        const prevId = prevEx.requestId || prevEx.albumId || prevEx.albumID;
        const currId = currEx.requestId || currEx.albumId || currEx.albumID;
        return prevId && currId && prevId === currId;
    }, [getParsedEx]);

    // Group messages - memoized for performance
    const groupedMessages = useMemo(() => {
        if (!messages.length) {
            return [];
        }

        const groups: GroupMessage[] = [];
        let currentGroup: GroupMessage = {
            items: [messages[0]],
            sendID: messages[0].sendID,
            sendTime: messages[0].sendTime,
            isRead: messages[0].isRead,
            readAt: messages[0].readAt
        };
        groups.push(currentGroup);

        for (let i = 1; i < messages.length; i++) {
            const msg = messages[i];
            const prev = messages[i - 1];

            // Skip consecutive revoked messages
            if (msg.contentType === MessageType.RevokeMessage && prev.contentType === MessageType.RevokeMessage) {
                continue;
            }

            // Check if can group (same sender, same request/album ID)
            const canGroup = canGroupMessages(prev, msg);

            if (canGroup) {
                currentGroup.items.push(msg);
                currentGroup.sendTime = msg.sendTime;
                currentGroup.isRead = msg.isRead;
                currentGroup.readAt = msg.readAt || currentGroup.readAt;
            } else {
                currentGroup = {
                    items: [msg],
                    sendID: msg.sendID,
                    sendTime: msg.sendTime,
                    isRead: msg.isRead,
                    readAt: msg.readAt
                };
                groups.push(currentGroup);
            }
        }

        // Group images into albums based on albumID in ex field
        const finalGroups: GroupMessage[] = [];

        for (const group of groups) {
            const albumMap: Record<string, MessageDto[]> = {};
            const albumOrder: string[] = [];
            const nonAlbumItems: MessageDto[] = [];

            for (const msg of group.items) {
                if (msg.contentType === MessageType.PictureMessage && msg.ex) {
                    const exData = getParsedEx(msg);
                    const albumId = exData.albumID;
                    if (albumId) {
                        if (!albumMap[albumId]) {
                            albumMap[albumId] = [];
                            albumOrder.push(albumId);
                        }
                        albumMap[albumId].push(msg);
                        continue;
                    }
                }
                nonAlbumItems.push(msg);
            }

            // Add non-album items (if any)
            if (nonAlbumItems.length > 0) {
                finalGroups.push({
                    items: nonAlbumItems,
                    sendID: group.sendID,
                    sendTime: group.sendTime,
                    isRead: group.isRead,
                    readAt: group.readAt
                });
            }

            // Add album groups (one entry per albumID)
            for (const albumId of albumOrder) {
                const albumImages = albumMap[albumId];
                if (!albumImages || albumImages.length === 0) continue;
                finalGroups.push({
                    items: albumImages,
                    sendID: group.sendID,
                    sendTime: albumImages[albumImages.length - 1].sendTime,
                    isRead: group.isRead,
                    readAt: group.readAt,
                    albumID: albumId,
                    albumImages
                });
            }
        }

        return finalGroups;
    }, [messages, canGroupMessages, getParsedEx]);

    // CRITICAL: Preserve scroll position on EVERY render when user is scrolled up
    // This catches ALL cases where scroll might jump - context updates, local updates, typing indicators, etc.
    // Using useLayoutEffect with NO dependencies to run synchronously on every render
    useLayoutEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        // Skip during initial load period (first 3 seconds)
        if (isInitialLoadRef.current) return;

        // If user has scrolled up and we have a saved position
        if (!isNearBottomRef.current && lastKnownScrollTopRef.current > 0) {
            const savedPosition = lastKnownScrollTopRef.current;
            const currentPosition = container.scrollTop;
            const distanceFromBottom = container.scrollHeight - currentPosition - container.clientHeight;

            // Detect if scroll jumped to bottom (current position is near bottom but saved position wasn't)
            const jumpedToBottom = distanceFromBottom < 100 && savedPosition < (container.scrollHeight - container.clientHeight - 100);

            if (jumpedToBottom) {
                ignoreScrollUntilRef.current = Date.now() + 500;
                container.scrollTop = savedPosition;
            }
        }
    }); // NO dependencies - runs on EVERY render

    // Sync real-time messages from context to local state
    useEffect(() => {
        // Early return if no room selected
        if (!selectedRoom?.groupID && !selectedRoom?.conversationID) {
            return;
        }

        if (contextMessages.length === 0) {
            return;
        }

        // Filter context messages for the current conversation
        // NOTE: We do NOT skip processed messages here anymore, because we need to check them for updates (read status)
        const relevantMessages = contextMessages.filter(m => {
            // For group messages (sessionType 3), groupID should match
            if (m.sessionType === 3 && selectedRoom?.groupID) {
                return m.groupID === selectedRoom.groupID;
            }
            // Fallback to checking both groupID and recvID
            return m.groupID === selectedRoom?.groupID || m.recvID === selectedRoom?.groupID;
        });

        if (relevantMessages.length === 0) {
            return;
        }

        // Filter to only displayable messages
        const newMessages = relevantMessages.filter(m => hasDisplayableContent(m));

        setMessages(prev => {
            let hasChanges = false;
            let nextMessages = prev;

            // 1. Identify updates and new messages
            newMessages.forEach(ctxMsg => {
                const existingIndex = findMessageIndexByIdentity(nextMessages, ctxMsg);
                if (existingIndex !== -1) {
                    const updated = upsertMessageByClientID(nextMessages, ctxMsg);
                    if (updated !== nextMessages) {
                        nextMessages = updated;
                        hasChanges = true;
                    }
                } else if (!processedMessageIdsRef.current.has(ctxMsg.clientMsgID)) {
                    // New message
                    nextMessages = upsertMessageByClientID(nextMessages, ctxMsg);
                    processedMessageIdsRef.current.add(ctxMsg.clientMsgID);
                    hasChanges = true;

                    // Handle scrolling for NEW messages only
                    const container = messagesContainerRef.current;
                    // Only auto-scroll if user is near the bottom
                    if (isNearBottomRef.current) {
                        pendingScrollRef.current = 'bottom';
                        setTimeout(() => {
                            setUnreadCount(0);
                            setShowNewMessageButton(false);
                        }, 0);
                    } else {
                        // User is reading history - show floating button with unread count
                        setTimeout(() => {
                            setUnreadCount(prevCount => prevCount + 1);
                            setShowNewMessageButton(true);
                        }, 0);

                        // CRITICAL: Save scroll position BEFORE adding messages
                        if (container) {
                            const savedScrollTop = container.scrollTop;
                            scrollRestoreDataRef.current = {
                                prevHeight: container.scrollHeight,
                                scrollTop: savedScrollTop
                            };
                            pendingScrollRef.current = 'preserve';

                            // Temporarily lock scroll position
                            const originalOverflow = container.style.overflow;
                            container.style.overflow = 'hidden';

                            queueMicrotask(() => {
                                requestAnimationFrame(() => {
                                    container.style.overflow = originalOverflow || '';
                                    container.scrollTop = savedScrollTop;
                                });
                            });
                        }
                    }
                }
            });

            if (!hasChanges) return prev;
            return [...nextMessages].sort((a, b) => a.sendTime - b.sendTime);
        });
    }, [contextMessages, selectedRoom, canGroupMessages, getParsedEx]);

    // Handle pending scroll after messages state updates
    // Using useLayoutEffect to scroll synchronously before browser paints
    useLayoutEffect(() => {
        if (pendingScrollRef.current === null) return;

        const container = messagesContainerRef.current;
        if (!container) {
            pendingScrollRef.current = null;
            return;
        }

        if (pendingScrollRef.current === 'bottom') {
            // Double-check: only scroll if user is actually near bottom
            if (!isNearBottomRef.current) {
                pendingScrollRef.current = null;
                return;
            }
            ignoreScrollUntilRef.current = Date.now() + 200;
            container.scrollTop = container.scrollHeight;
        } else if (pendingScrollRef.current === 'restore' && scrollRestoreDataRef.current) {
            ignoreScrollUntilRef.current = Date.now() + 500;
            const scrollDiff = container.scrollHeight - scrollRestoreDataRef.current.prevHeight;
            container.scrollTop = scrollDiff;
            scrollRestoreDataRef.current = null;
            isNearBottomRef.current = false;
        } else if (pendingScrollRef.current === 'preserve' && scrollRestoreDataRef.current) {
            // PRESERVE scroll position when new messages added at bottom while user is reading history
            const savedScrollTop = scrollRestoreDataRef.current.scrollTop;
            if (savedScrollTop !== undefined) {
                ignoreScrollUntilRef.current = Date.now() + 1000;
                container.scrollTop = savedScrollTop;

                const containerRef = container;
                const positionToPreserve = savedScrollTop;

                // Set up a scroll guard to catch any unwanted scroll changes
                const scrollGuard = () => {
                    if (Math.abs(containerRef.scrollTop - positionToPreserve) > 10) {
                        containerRef.scrollTop = positionToPreserve;
                    }
                };
                containerRef.addEventListener('scroll', scrollGuard);
                setTimeout(() => containerRef.removeEventListener('scroll', scrollGuard), 200);

                // Multiple preservation attempts
                requestAnimationFrame(() => {
                    if (containerRef.scrollTop !== positionToPreserve) {
                        containerRef.scrollTop = positionToPreserve;
                    }
                    requestAnimationFrame(() => {
                        if (containerRef.scrollTop !== positionToPreserve) {
                            containerRef.scrollTop = positionToPreserve;
                        }
                    });
                });

                setTimeout(() => {
                    if (containerRef.scrollTop !== positionToPreserve) {
                        containerRef.scrollTop = positionToPreserve;
                    }
                }, 50);

                setTimeout(() => {
                    if (containerRef.scrollTop !== positionToPreserve) {
                        containerRef.scrollTop = positionToPreserve;
                    }
                }, 100);
            }
            scrollRestoreDataRef.current = null;
            isNearBottomRef.current = false;
        }

        pendingScrollRef.current = null;
    }, [messages]);

    // Reset initial load flag when conversation changes
    useEffect(() => {
        isInitialLoadRef.current = true;
        initialMessageCountRef.current = 0; // Reset for new conversation
    }, [selectedRoom?.conversationID]);

    // Handle initial load scroll with ResizeObserver for images
    // This effect should ONLY run once per conversation load, not on every message change
    useEffect(() => {
        // Only run when loading finishes and we have messages
        if (isLoading || messages.length === 0) return;

        // Only apply during initial load - skip if already completed
        if (!isInitialLoadRef.current) return;

        // Store initial message count ONCE to detect new messages vs image loading
        // If already set, this is a re-run due to new messages - skip entirely
        if (initialMessageCountRef.current > 0) {
            return; // Already initialized, don't create new observers/timeouts
        }
        initialMessageCountRef.current = messages.length;

        const container = messagesContainerRef.current;
        if (!container) return;

        // Scroll to bottom function
        const scrollToBottom = () => {
            if (!isInitialLoadRef.current) return;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        };

        // Wait for all images to load then scroll
        const waitForImagesAndScroll = () => {
            const images = container.querySelectorAll('img');
            const imagePromises: Promise<void>[] = [];

            images.forEach(img => {
                if (!img.complete) {
                    imagePromises.push(new Promise(resolve => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        setTimeout(resolve, 2000); // Timeout fallback
                    }));
                }
            });

            Promise.all(imagePromises).then(() => {
                if (isInitialLoadRef.current && container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        };

        // Immediate scroll
        scrollToBottom();

        // Wait for images then scroll
        waitForImagesAndScroll();

        // Also scroll after delays to handle late renders (extended delays for image loading)
        const timeouts = [50, 100, 200, 400, 800, 1200, 1800].map(delay =>
            setTimeout(scrollToBottom, delay)
        );

        // Use ResizeObserver to catch images loading
        let scrollTimeout: NodeJS.Timeout;
        const observer = new ResizeObserver(() => {
            if (!isInitialLoadRef.current) return;
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(scrollToBottom, 30);
        });

        observer.observe(container);

        // Mark initial load as complete after 3 seconds (matches ignoreScrollUntilRef)
        const completionTimeout = setTimeout(() => {
            isInitialLoadRef.current = false;
            observer.disconnect();
        }, 3000);

        return () => {
            observer.disconnect();
            clearTimeout(scrollTimeout);
            timeouts.forEach(t => clearTimeout(t));
            clearTimeout(completionTimeout);
        };
    }, [isLoading, messages.length, selectedRoom?.conversationID]);

    // Handle layout/container resize (e.g., switching to fullscreen)
    // This ensures scroll stays at bottom when container size changes significantly
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container || messages.length === 0) return;

        let lastWidth = container.clientWidth;
        let lastHeight = container.clientHeight;
        const pendingTimeouts: NodeJS.Timeout[] = [];

        const scrollToBottomAfterLoad = () => {
            if (!container) return;

            // Find all images in the container
            const images = container.querySelectorAll('img');
            const imagePromises: Promise<void>[] = [];

            images.forEach(img => {
                if (!img.complete) {
                    imagePromises.push(new Promise(resolve => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        // Timeout fallback in case image loading hangs
                        setTimeout(resolve, 2000);
                    }));
                }
            });

            // Wait for all images to load, then scroll
            Promise.all(imagePromises).then(() => {
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });

            // Also do immediate scroll and delayed scrolls as backup
            container.scrollTop = container.scrollHeight;

            // Multiple attempts with increasing delays to handle late-loading content
            [100, 300, 500, 800, 1200].forEach(delay => {
                const t = setTimeout(() => {
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                }, delay);
                pendingTimeouts.push(t);
            });
        };

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newWidth = entry.contentRect.width;
                const newHeight = entry.contentRect.height;

                // Detect significant size change (more than 100px difference indicates layout change)
                const widthChanged = Math.abs(newWidth - lastWidth) > 100;
                const heightChanged = Math.abs(newHeight - lastHeight) > 100;

                if (widthChanged || heightChanged) {
                    // Always scroll to bottom on significant layout change (e.g., switching to fullscreen)
                    // User expects to be at the bottom when opening fullscreen
                    ignoreScrollUntilRef.current = Date.now() + 1500;
                    isNearBottomRef.current = true; // Reset to near bottom on layout change
                    requestAnimationFrame(() => {
                        scrollToBottomAfterLoad();
                    });
                }

                lastWidth = newWidth;
                lastHeight = newHeight;
            }
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            pendingTimeouts.forEach(t => clearTimeout(t));
        };
    }, [messages.length]);

    // Close emoji picker on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    // Close attachment menu on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target as Node)) {
                setShowAttachmentMenu(false);
            }
        };

        if (showAttachmentMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAttachmentMenu]);

    // Close GIF picker on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (gifPickerRef.current && !gifPickerRef.current.contains(event.target as Node)) {
                setShowGifPicker(false);
            }
        };

        if (showGifPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showGifPicker]);

    // Load featured GIFs when picker opens
    useEffect(() => {
        if (showGifPicker && gifs.length === 0) {
            loadFeaturedGifs();
        }
    }, [showGifPicker]);

    // Search GIFs with debounce
    useEffect(() => {
        if (!showGifPicker) return;

        if (!gifSearchQuery) {
            loadFeaturedGifs();
            return;
        }

        const timer = setTimeout(() => {
            handleGifSearch();
        }, 500);

        return () => clearTimeout(timer);
    }, [gifSearchQuery, showGifPicker]);

    // Auto-save draft
    useEffect(() => {
        if (!selectedRoom?.conversationID) return;

        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

        draftTimerRef.current = setTimeout(() => {
            saveDraft(selectedRoom.conversationID || '', messageText);
        }, 1000);

        return () => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        };
    }, [messageText, selectedRoom?.conversationID]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        };
    }, [audioPreviewUrl]);

    // Handle click outside context menu
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;

            // Don't close if clicking inside reaction picker
            if (reactionPickerRef.current && reactionPickerRef.current.contains(target)) {
                return;
            }

            // Don't close if clicking inside context menu
            if (contextMenuRef.current && contextMenuRef.current.contains(target)) {
                return;
            }

            setContextMenu(null);
            setShowReactionPicker(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
        }
    };

    // Load more messages on scroll with threshold for better UX
    const handleScroll = useCallback(async (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;

        // Calculate distance from bottom
        const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        const wasNearBottom = isNearBottomRef.current;
        const isNearBottom = distanceFromBottom < 100;

        // During programmatic scrolls, only skip if scrolling TO bottom (not away from it)
        const isProgrammaticScroll = Date.now() < ignoreScrollUntilRef.current;

        if (isProgrammaticScroll && isNearBottom) {
            return;
        }

        // ALWAYS update isNearBottomRef for user scrolls
        isNearBottomRef.current = isNearBottom;

        // Track scroll position for restoration when not near bottom
        if (!isNearBottom) {
            lastKnownScrollTopRef.current = target.scrollTop;
        }

        // Clear new message indicator when user scrolls to bottom
        if (isNearBottomRef.current && !wasNearBottom) {
            setUnreadCount(0);
            setShowNewMessageButton(false);
        }

        // Trigger load when near top (within 50px) for smoother experience
        const nearTop = target.scrollTop <= 50;

        if (nearTop && !isLoadingMore && !noMoreMessagesRef.current && oldestMessageIDRef.current) {
            setIsLoadingMore(true);

            // Save current scroll height before loading
            const container = messagesContainerRef.current;
            const prevScrollHeight = container?.scrollHeight || 0;

            try {
                const olderMsgs = await loadMessages(selectedRoom!.conversationID || '', oldestMessageIDRef.current);

                if (olderMsgs.length > 0) {
                    // Get the real message ID (handles album messages)
                    oldestMessageIDRef.current = getRealMessageID(olderMsgs[0]);

                    // Set up scroll restoration
                    scrollRestoreDataRef.current = { prevHeight: prevScrollHeight };
                    pendingScrollRef.current = 'restore';

                    // Update messages
                    setMessages(prev => dedupeMessagesByIdentity([...olderMsgs, ...prev]));
                } else {
                    noMoreMessagesRef.current = true;
                }
            } catch (error) {
                console.error('Failed to load older messages:', error);
            } finally {
                setIsLoadingMore(false);
            }
        }
    }, [isLoadingMore, loadMessages, selectedRoom]);

    const clearTypingStopTimer = useCallback(() => {
        if (typingStopTimerRef.current) {
            clearTimeout(typingStopTimerRef.current);
            typingStopTimerRef.current = null;
        }
    }, []);

    const clearTypingRefreshTimer = useCallback(() => {
        if (typingRefreshTimerRef.current) {
            clearInterval(typingRefreshTimerRef.current);
            typingRefreshTimerRef.current = null;
        }
        typingRefreshConversationRef.current = null;
    }, []);

    const startTypingRefreshTimer = useCallback((conversationID: string) => {
        if (
            typingRefreshTimerRef.current &&
            typingRefreshConversationRef.current === conversationID
        ) {
            return;
        }

        clearTypingRefreshTimer();
        typingRefreshConversationRef.current = conversationID;
        typingRefreshTimerRef.current = setInterval(() => {
            if (
                activeTypingConversationRef.current !== conversationID ||
                !hasSentTypingStartRef.current
            ) {
                clearTypingRefreshTimer();
                return;
            }

            sendTypingStatus(conversationID, true);
        }, TYPING_REFRESH_MS);
    }, [clearTypingRefreshTimer, sendTypingStatus]);

    const stopTypingIndicator = useCallback((conversationID?: string) => {
        clearTypingStopTimer();
        clearTypingRefreshTimer();
        const targetConversationID = conversationID || activeTypingConversationRef.current;
        if (!targetConversationID) return;
        if (!hasSentTypingStartRef.current) return;

        sendTypingStatus(targetConversationID, false);
        hasSentTypingStartRef.current = false;
    }, [clearTypingRefreshTimer, clearTypingStopTimer, sendTypingStatus]);

    const scheduleTypingStop = useCallback((conversationID: string) => {
        clearTypingStopTimer();
        typingStopTimerRef.current = setTimeout(() => {
            typingStopTimerRef.current = null;
            if (activeTypingConversationRef.current !== conversationID) return;
            if (!hasSentTypingStartRef.current) return;

            clearTypingRefreshTimer();
            sendTypingStatus(conversationID, false);
            hasSentTypingStartRef.current = false;
        }, TYPING_DEBOUNCE_MS);
    }, [clearTypingRefreshTimer, clearTypingStopTimer, sendTypingStatus]);

    // Message input handling
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;
        setMessageText(text);
        adjustTextareaHeight();

        // Check for @mentions using the active token (text since last whitespace).
        // Use the first "@" in the token so queries like "@name@domain" still work.
        const cursorPosition = e.target.selectionStart ?? text.length;
        const textBeforeCursor = text.slice(0, cursorPosition);
        const tokenMatch = textBeforeCursor.match(/(?:^|\s)(\S*)$/);
        const activeToken = tokenMatch?.[1] || '';
        const firstAtIndex = activeToken.indexOf('@');

        if (firstAtIndex !== -1 && selectedRoom?.members) {
            const searchTerm = activeToken.substring(firstAtIndex + 1).toLowerCase();
            const filtered = selectedRoom.members.filter(m =>
                m.userID.toLowerCase().includes(searchTerm) ||
                (m.nickname && m.nickname.toLowerCase().includes(searchTerm)) ||
                (m.email && m.email.toLowerCase().includes(searchTerm))
            );
            setFilteredUsers(filtered);
            setShowUserSuggestions(filtered.length > 0);
        } else {
            setShowUserSuggestions(false);
        }

        const conversationID = selectedRoom?.conversationID;
        if (!conversationID) return;

        // Room switch guard: stop old conversation if still marked as typing.
        if (
            activeTypingConversationRef.current &&
            activeTypingConversationRef.current !== conversationID &&
            hasSentTypingStartRef.current
        ) {
            sendTypingStatus(activeTypingConversationRef.current, false);
            hasSentTypingStartRef.current = false;
            clearTypingStopTimer();
            clearTypingRefreshTimer();
        }

        activeTypingConversationRef.current = conversationID;

        if (!text.trim()) {
            stopTypingIndicator(conversationID);
            return;
        }

        if (!hasSentTypingStartRef.current) {
            sendTypingStatus(conversationID, true);
            hasSentTypingStartRef.current = true;
        }
        startTypingRefreshTimer(conversationID);
        scheduleTypingStop(conversationID);
    };

    const handleInputBlur = () => {
        stopTypingIndicator(selectedRoom?.conversationID);
    };

    useEffect(() => {
        const nextConversationID = selectedRoom?.conversationID || null;
        const previousConversationID = activeTypingConversationRef.current;

        if (
            previousConversationID &&
            previousConversationID !== nextConversationID &&
            hasSentTypingStartRef.current
        ) {
            sendTypingStatus(previousConversationID, false);
        }

        clearTypingStopTimer();
        clearTypingRefreshTimer();
        hasSentTypingStartRef.current = false;
        activeTypingConversationRef.current = nextConversationID;
    }, [clearTypingRefreshTimer, clearTypingStopTimer, selectedRoom?.conversationID, sendTypingStatus]);

    useEffect(() => {
        return () => {
            stopTypingIndicator(activeTypingConversationRef.current || undefined);
        };
    }, [stopTypingIndicator]);

    // Search input handling
    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInputValue(value);

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

        if (!value.trim()) {
            searchRequestSeqRef.current += 1;
            setSearchResults([]);
            setIsSearchLoading(false);
            return;
        }

        setIsSearchLoading(true);
        searchDebounceRef.current = setTimeout(() => void performSearch(value), 300);
    };

    const highlightText = (text: string, term: string): string => {
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(regex, '<mark>$1</mark>');
    };

    const mapMessageToSearchResult = (msg: MessageDto, query: string): SearchResult | null => {
        const searchTerm = query.toLowerCase();
        const textContent = getMessageTextContent(msg);

        // Search text-like messages
        if (
            (msg.contentType === MessageType.TextMessage ||
                msg.contentType === MessageType.AtTextMessage ||
                msg.contentType === MessageType.AdvancedTextMessage) &&
            textContent
        ) {
            if (textContent.toLowerCase().includes(searchTerm)) {
                return {
                    messageId: msg.clientMsgID,
                    type: 'text',
                    snippet: highlightText(textContent, query),
                    timestamp: msg.sendTime,
                    senderNickname: msg.senderNickname || msg.sendID
                };
            }
        }

        // Search quote messages
        if (msg.contentType === MessageType.QuoteMessage && msg.quoteElem?.text) {
            if (msg.quoteElem.text.toLowerCase().includes(searchTerm)) {
                return {
                    messageId: msg.clientMsgID,
                    type: 'text',
                    snippet: highlightText(msg.quoteElem.text, query),
                    timestamp: msg.sendTime,
                    senderNickname: msg.senderNickname || msg.sendID
                };
            }
        }

        // Search file names
        if (msg.contentType === MessageType.FileMessage && msg.fileElem?.fileName) {
            if (msg.fileElem.fileName.toLowerCase().includes(searchTerm)) {
                return {
                    messageId: msg.clientMsgID,
                    type: 'file',
                    snippet: `File: ${highlightText(msg.fileElem.fileName, query)}`,
                    timestamp: msg.sendTime,
                    senderNickname: msg.senderNickname || msg.sendID
                };
            }
        }

        return null;
    };

    const performSearch = useCallback(async (query: string) => {
        const normalizedQuery = query.trim();
        if (!normalizedQuery) {
            setSearchResults([]);
            setIsSearchLoading(false);
            return;
        }

        const requestSeq = ++searchRequestSeqRef.current;
        const dedupedResults = new Map<string, SearchResult>();

        const addResult = (result: SearchResult | null) => {
            if (!result) return;
            if (!dedupedResults.has(result.messageId)) {
                dedupedResults.set(result.messageId, result);
            }
        };

        // 1) Search full local history for this conversation via SDK.
        // 2) Merge with currently loaded messages to cover all renderable snippets.
        try {
            const conversationID = selectedRoom?.conversationID;
            if (conversationID) {
                const historyItems = await searchMessages(normalizedQuery, {
                    conversationID,
                    count: 200,
                    messageTypeList: [
                        MessageType.TextMessage,
                        MessageType.AtTextMessage,
                        MessageType.AdvancedTextMessage,
                        MessageType.QuoteMessage,
                        MessageType.FileMessage,
                    ],
                });

                const conversationMatches =
                    historyItems.find(item => item.conversationID === conversationID)?.messageList || [];
                for (const msg of conversationMatches) {
                    addResult(mapMessageToSearchResult(msg, normalizedQuery));
                }
            }
        } catch (error) {
            console.error('Error searching message history:', error);
        }

        for (const msg of messages) {
            addResult(mapMessageToSearchResult(msg, normalizedQuery));
        }

        if (requestSeq !== searchRequestSeqRef.current) return;

        const results = Array.from(dedupedResults.values());
        results.sort((a, b) => b.timestamp - a.timestamp);

        setSearchResults(results.slice(0, 50));
        setIsSearchLoading(false);
    }, [messages, searchMessages, selectedRoom?.conversationID]);

    const clearSearch = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchRequestSeqRef.current += 1;
        setSearchInputValue('');
        setSearchResults([]);
        setIsSearchLoading(false);
        setHighlightedMessageId(null);
    };

    const jumpToMessage = useCallback((messageId: string) => {
        const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedMessageId(messageId);
            setTimeout(() => setHighlightedMessageId(null), 2500);
        }
    }, []);

    // Scroll to bottom and clear new message indicator (like WhatsApp, Telegram, Slack)
    const scrollToBottomAndClear = useCallback(() => {
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            isNearBottomRef.current = true;
            setUnreadCount(0);
            setShowNewMessageButton(false);
        }
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const conversationID = selectedRoom?.conversationID;
        if (conversationID && !e.nativeEvent.isComposing) {
            // Ignore pure modifier keys. Everything else counts as activity and should
            // reset the typing-stop countdown while a draft exists.
            const isModifierKey =
                e.key === 'Shift' ||
                e.key === 'Control' ||
                e.key === 'Alt' ||
                e.key === 'Meta';

            if (!isModifierKey) {
                // Room switch guard: stop old conversation if still marked as typing.
                if (
                    activeTypingConversationRef.current &&
                    activeTypingConversationRef.current !== conversationID &&
                    hasSentTypingStartRef.current
                ) {
                    sendTypingStatus(activeTypingConversationRef.current, false);
                    hasSentTypingStartRef.current = false;
                    clearTypingStopTimer();
                    clearTypingRefreshTimer();
                }

                activeTypingConversationRef.current = conversationID;

                const currentValue = (textareaRef.current?.value ?? messageText).trim();
                const keyCanAddText = e.key.length === 1 || e.key === 'Enter';
                const shouldStartTyping = currentValue.length > 0 || keyCanAddText;

                // Initiate typing immediately on first typing activity.
                if (shouldStartTyping && !hasSentTypingStartRef.current) {
                    sendTypingStatus(conversationID, true);
                    hasSentTypingStartRef.current = true;
                }

                // Every key activity resets the 5s stop timer.
                if (hasSentTypingStartRef.current) {
                    startTypingRefreshTimer(conversationID);
                    scheduleTypingStop(conversationID);
                }
            }
        }

        // Enter always adds a new line; use send button to send
        if (e.key === 'Enter') {
            // Allow default behavior (new line in textarea)
            // Auto-adjust height after the new line is added
            setTimeout(adjustTextareaHeight, 0);
        }
    };

    const selectUser = (user: GroupMemberDto) => {
        const cursorPosition = textareaRef.current?.selectionStart ?? messageText.length;
        const textBeforeCursor = messageText.slice(0, cursorPosition);
        const textAfterCursor = messageText.slice(cursorPosition);
        const tokenMatch = textBeforeCursor.match(/(?:^|\s)(\S*)$/);
        const activeToken = tokenMatch?.[1] || '';
        const firstAtIndex = activeToken.indexOf('@');

        if (firstAtIndex === -1) {
            setShowUserSuggestions(false);
            return;
        }

        const activeTokenStart = textBeforeCursor.length - activeToken.length;
        const replaceStart = activeTokenStart + firstAtIndex;
        const mentionLabel = (user.email || user.userID).trim();
        // Keep full email text without a leading "@" in the composed message.
        const insertion = `${mentionLabel} `;
        const cleanedAfterCursor = textAfterCursor.replace(/^\S*/, '');
        const nextText = messageText.slice(0, replaceStart) + insertion + cleanedAfterCursor;
        const nextCursor = replaceStart + insertion.length;

        setMessageText(nextText);
        setShowUserSuggestions(false);

        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const extractMentionPayload = useCallback((text: string) => {
        const members = selectedRoom?.members || [];
        if (!text || members.length === 0) {
            return { atUserIDList: [] as string[], atUsersInfo: [] as { atUserID: string; groupNickname: string }[] };
        }

        const mentionTokens = text
            .split(/\s+/)
            .map(token => token.trim().replace(/[.,!?;:]+$/g, ''))
            .filter(Boolean)
            .map(token => {
                if (token.startsWith('@')) {
                    return token.slice(1).toLowerCase();
                }
                // Support email mentions inserted without leading "@"
                if (token.includes('@')) {
                    return token.toLowerCase();
                }
                return '';
            })
            .filter(Boolean);

        if (mentionTokens.length === 0) {
            return { atUserIDList: [] as string[], atUsersInfo: [] as { atUserID: string; groupNickname: string }[] };
        }

        const tokenSet = new Set(mentionTokens);
        const matchedByUserID = new Map<string, GroupMemberDto>();
        members.forEach(member => {
            const candidates = [
                member.email,
                member.userID,
                member.nickname,
            ]
                .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
                .filter(Boolean);

            if (candidates.some(candidate => tokenSet.has(candidate))) {
                matchedByUserID.set(member.userID, member);
            }
        });

        const matchedMembers = Array.from(matchedByUserID.values());
        return {
            atUserIDList: matchedMembers.map(member => member.userID),
            atUsersInfo: matchedMembers.map(member => ({
                atUserID: member.userID,
                groupNickname: member.nickname || member.email || member.userID,
            })),
        };
    }, [selectedRoom?.members]);

    // Send message
    const handleSendMessage = async () => {
        if (!selectedRoom?.groupID) return;
        if (!messageText.trim() && !selectedFiles.length && !recordedAudio && !selectedLocation) return;

        const requestId = createUniqueId();
        const conversationID = selectedRoom.conversationID;

        // Sending a real message should immediately stop typing indicator.
        if (conversationID) {
            stopTypingIndicator(conversationID);
        }

        // Auto-open room if customer and room is closed
        if (userType === UserType.Customer && selectedRoom.introduction === 'close') {
            await openGroup(selectedRoom.groupID);
        }

        // Mark files as uploading
        setSelectedFiles(prev => prev.map(f => ({ ...f, isUploading: true })));

        // Mark audio as uploading (using isLoading state)
        const hasAudio = !!recordedAudio;
        const hasLocation = !!selectedLocation;
        if (hasAudio || hasLocation) {
            setIsLoading(true);
        }

        try {
            const sendTasks: Array<{ run: () => Promise<MessageDto | null>; fallback: MessageDto }> = [];
            const failedMessage = (content: string, sendError: string) => createLocalFailedMessage({
                currentUserID,
                groupID: selectedRoom.groupID,
                requestId,
                content,
                sendError,
            });

            // Text message
            if (messageText.trim()) {
                const mentionPayload = extractMentionPayload(messageText);
                if (replyingTo) {
                    sendTasks.push({
                        run: () => sendReplyMessage({
                            text: messageText,
                            replyItem: replyingTo,
                            groupID: selectedRoom.groupID,
                            requestId
                        }),
                        fallback: failedMessage(messageText, t.messageSendFailed),
                    });
                } else {
                    sendTasks.push({
                        run: () => sendTextMessage({
                            text: messageText,
                            groupID: selectedRoom.groupID,
                            requestId,
                            atUserIDList: mentionPayload.atUserIDList,
                            atUsersInfo: mentionPayload.atUsersInfo,
                        }),
                        fallback: failedMessage(messageText, t.messageSendFailed),
                    });
                }
            }

            // File messages
            for (const file of selectedFiles) {
                if (file.type === 'image') {
                    sendTasks.push({
                        run: () => sendImageMessage({ file: file.file, groupID: selectedRoom.groupID, requestId }),
                        fallback: failedMessage(file.name, t.photoUploadFailed),
                    });
                } else if (file.type === 'video' && file.thumbnailFile) {
                    const thumbnailFile = file.thumbnailFile;
                    sendTasks.push({
                        run: () => sendVideoMessage({
                            file: file.file,
                            groupID: selectedRoom.groupID,
                            thumbnail: thumbnailFile,
                            duration: file.duration || 0,
                            requestId
                        }),
                        fallback: failedMessage(file.name, t.attachmentUploadFailed),
                    });
                } else {
                    sendTasks.push({
                        run: () => sendFileMessage({ file: file.file, groupID: selectedRoom.groupID, requestId }),
                        fallback: failedMessage(file.name, t.attachmentUploadFailed),
                    });
                }
            }

            // Audio message
            if (recordedAudio) {
                sendTasks.push({
                    run: () => sendSoundMessage({
                        file: recordedAudio.file,
                        groupID: selectedRoom.groupID,
                        duration: recordedAudio.duration,
                        requestId
                    }),
                    fallback: failedMessage(t.voice, t.attachmentUploadFailed),
                });
            }

            // Location message
            if (selectedLocation) {
                sendTasks.push({
                    run: () => sendLocationMessage({
                        ...selectedLocation,
                        groupID: selectedRoom.groupID,
                        requestId
                    }),
                    fallback: failedMessage(selectedLocation.description, t.messageSendFailed),
                });
            }

            // Wait for all and add to messages
            const results = await Promise.all(sendTasks.map(async task => {
                try {
                    return await task.run() || task.fallback;
                } catch (error) {
                    console.error('Failed to send message task:', error);
                    return task.fallback;
                }
            }));
            const sentMsgs = results.filter((m): m is MessageDto => m !== null);

            if (sentMsgs.length) {
                // Mark sent messages as processed to prevent duplicate from context
                sentMsgs.forEach(m => processedMessageIdsRef.current.add(m.clientMsgID));

                // Force scroll to bottom for sent messages
                isNearBottomRef.current = true;
                pendingScrollRef.current = 'bottom';

                setMessages(prev => {
                    return sentMsgs.reduce(
                        (next, message) => upsertMessageByClientID(next, message),
                        prev
                    );
                });
                clearDraft(selectedRoom.conversationID || '');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            // Reset inputs and loading states
            resetInputs();
            setIsLoading(false);
        }
    };

    const resetInputs = () => {
        setMessageText('');
        setSelectedFiles([]);
        setFileUploadError('');
        setReplyingTo(null);
        setRecordedAudio(null);
        setSelectedLocation(null);
        setRecordingDuration(0);
        if (audioPreviewUrl) {
            URL.revokeObjectURL(audioPreviewUrl);
            setAudioPreviewUrl(null);
        }
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    // File selection
    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'document') => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const sizeLimitError = getFileSizeLimitErrorMessage(t.fileSizeExceedsLimit);
        const validFiles = files.filter(file => file.size <= FileConfig.MAX_SIZE);
        const oversizedFiles = files.filter(file => file.size > FileConfig.MAX_SIZE);

        if (oversizedFiles.length > 0) {
            setFileUploadError(sizeLimitError);
        } else {
            setFileUploadError('');
        }

        if (validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        // Check if multiple images selected - send as album
        if (type === 'image' && validFiles.length > 1 && selectedRoom) {
            const validImageFiles = validFiles;

            if (validImageFiles.length > 1) {
                // Send as album with loading state
                setIsSendingAlbum(true);
                setAlbumProgress({ current: 0, total: validImageFiles.length });

                try {
                    // Force scroll to bottom for sent album
                    isNearBottomRef.current = true;
                    pendingScrollRef.current = 'bottom';

                    const sentMsgs = await sendAlbumMessage({
                        files: validImageFiles,
                        groupID: selectedRoom.groupID || '',
                        onProgress: (current, total) => {
                            setAlbumProgress({ current, total });
                        }
                    });

                    // Add sent messages to local state with complete URL data
                    if (sentMsgs && sentMsgs.length > 0) {
                        sentMsgs.forEach(m => processedMessageIdsRef.current.add(m.clientMsgID));

                        setMessages(prev => {
                            return sentMsgs.reduce(
                                (next, message) => upsertMessageByClientID(next, message),
                                prev
                            );
                        });
                    } else {
                        const fallback = createLocalFailedMessage({
                            currentUserID,
                            groupID: selectedRoom.groupID,
                            requestId: createUniqueId(),
                            content: `${validImageFiles.length} ${t.photos}`,
                            sendError: t.photoUploadFailed,
                        });
                        setMessages(prev => upsertMessageByClientID(prev, fallback));
                    }
                } catch (error) {
                    console.error('Error sending album:', error);
                    const fallback = createLocalFailedMessage({
                        currentUserID,
                        groupID: selectedRoom.groupID,
                        requestId: createUniqueId(),
                        content: `${validImageFiles.length} ${t.photos}`,
                        sendError: t.photoUploadFailed,
                    });
                    setMessages(prev => upsertMessageByClientID(prev, fallback));
                } finally {
                    setIsSendingAlbum(false);
                    setAlbumProgress(null);
                }

                e.target.value = '';
                return;
            }
        }

        // Single file or multiple non-image files - process individually
        for (const file of validFiles) {
            const selected: SelectedFile = {
                file,
                name: file.name,
                size: file.size,
                type,
                isUploading: false
            };

            if (type === 'image') {
                selected.preview = await createImagePreview(file);
            } else if (type === 'video') {
                const metadata = await getVideoMetadata(file);
                selected.preview = metadata.thumbnailDataUrl;
                selected.thumbnailFile = metadata.thumbnailFile;
                selected.duration = metadata.duration;
            }

            setSelectedFiles(prev => [...prev, selected]);
        }

        e.target.value = '';
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Audio recording
    const toggleRecording = async () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeTypes = ['audio/webm', 'audio/ogg', 'audio/mp4'];
            const selectedMime = mimeTypes.find(t => MediaRecorder.isTypeSupported(t)) || 'audio/webm';

            const recorder = new MediaRecorder(stream, { mimeType: selectedMime });
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: selectedMime });
                const duration = recordingDuration;
                const ext = selectedMime.includes('webm') ? 'webm' : selectedMime.includes('ogg') ? 'ogg' : 'm4a';
                const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: selectedMime });

                setRecordedAudio({ file, duration });
                setAudioPreviewUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach(t => t.stop());
            };

            recorder.start();
            setIsRecording(true);
            setRecordingDuration(0);

            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(d => d + 1);
            }, 1000);
        } catch (err) {
            console.error('Failed to start recording:', err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current?.state !== 'inactive') {
            mediaRecorderRef.current?.stop();
        }
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
        }
        setIsRecording(false);
    };

    const deleteRecording = () => {
        if (isRecording) {
            mediaRecorderRef.current?.stop();
            setIsRecording(false);
        }
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
        setRecordedAudio(null);
        setAudioPreviewUrl(null);
        setRecordingDuration(0);
    };

    const toggleAudioPlayback = () => {
        if (!audioPreviewRef.current) return;
        if (isAudioPlaying) {
            audioPreviewRef.current.pause();
            setIsAudioPlaying(false);
        } else {
            audioPreviewRef.current.play();
            setIsAudioPlaying(true);
            audioPreviewRef.current.onended = () => setIsAudioPlaying(false);
        }
    };

    // Location
    const getLocation = () => {
        if (!navigator.geolocation) {
            console.error('Geolocation not supported');
            return;
        }

        setIsGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setSelectedLocation({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    description: `Lat: ${pos.coords.latitude.toFixed(6)}, Lng: ${pos.coords.longitude.toFixed(6)}`
                });
                setIsGettingLocation(false);
            },
            (err) => {
                console.error('Location error:', err);
                setIsGettingLocation(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    // Context menu
    const handleContextMenu = (e: React.MouseEvent, group: GroupMessage) => {
        e.preventDefault();
        e.stopPropagation();

        // Calculate position to keep menu within viewport
        const menuWidth = 290; // Approximate width of context menu
        const menuHeight = 160; // Approximate height of context menu with reactions
        const padding = 10;

        let x = e.clientX;
        let y = e.clientY;

        // Adjust horizontal position if menu would go off-screen
        if (x + menuWidth + padding > window.innerWidth) {
            x = window.innerWidth - menuWidth - padding;
        }
        if (x < padding) {
            x = padding;
        }

        // Adjust vertical position if menu would go off-screen
        if (y + menuHeight + padding > window.innerHeight) {
            y = window.innerHeight - menuHeight - padding;
        }
        if (y < padding) {
            y = padding;
        }

        setContextMenu({ x, y, message: group });
    };

    const handleReply = () => {
        if (contextMenu?.message.items[0]) {
            setReplyingTo(contextMenu.message.items[0]);
            textareaRef.current?.focus();
        }
        setContextMenu(null);
    };

    const handleRevoke = async () => {
        if (!contextMenu?.message || !selectedRoom?.conversationID) return;

        // Check if message is within 2 minutes (revoke time limit)
        const firstMsg = contextMenu.message.items[0];
        const messageAge = Date.now() - firstMsg.sendTime;

        if (messageAge > MessageConfig.REVOKE_TIME_LIMIT_MS) {
            // Message is too old to revoke, silently close menu
            setContextMenu(null);
            return;
        }

        for (const msg of contextMenu.message.items) {
            await revokeMessage({
                clientMsgID: msg.clientMsgID,
                conversationID: selectedRoom.conversationID
            });
            setMessages(prev =>
                prev.map(m => m.clientMsgID === msg.clientMsgID
                    ? { ...m, contentType: MessageType.RevokeMessage }
                    : m
                )
            );
        }
        setContextMenu(null);
    };

    const handleDeleteForMe = async () => {
        if (!contextMenu?.message || !selectedRoom?.conversationID) return;

        for (const msg of contextMenu.message.items) {
            await deleteMessageForMe({
                clientMsgID: msg.clientMsgID,
                conversationID: selectedRoom.conversationID
            });
            // Remove the message from local state
            setMessages(prev => prev.filter(m => m.clientMsgID !== msg.clientMsgID));
        }
        setContextMenu(null);
    };

    // Handle reaction
    const handleReaction = async (emoji: string) => {
        if (!contextMenu?.message || !selectedRoom?.groupID) return;

        const messageId = contextMenu.message.items[0].clientMsgID;
        await sendReaction({
            emoji,
            messageId,
            groupID: selectedRoom.groupID
        });
        setContextMenu(null);
    };

    // View attachments
    const viewImage = useCallback((msg: MessageDto) => {
        if (!msg.pictureElem) return;
        const allImages = messages
            .filter(m => m.contentType === MessageType.PictureMessage)
            .map(m => m.pictureElem!.sourcePicture.url);
        const index = allImages.indexOf(msg.pictureElem.sourcePicture.url);
        openImageViewer(allImages, index);
    }, [messages, openImageViewer]);

    const viewVideo = useCallback((msg: MessageDto) => {
        if (!msg.videoElem) return;
        const allVideos = messages
            .filter(m => m.contentType === MessageType.VideoMessage)
            .map(m => m.videoElem!.videoUrl);
        const index = allVideos.indexOf(msg.videoElem.videoUrl);
        openVideoViewer(allVideos, index);
    }, [messages, openVideoViewer]);

    const downloadFile = useCallback((msg: MessageDto) => {
        let url = '';
        let fileName = 'download';

        if (msg.pictureElem) {
            url = msg.pictureElem.sourcePicture.url;
            fileName = msg.pictureElem.sourcePicture.uuid + '.jpg';
        } else if (msg.videoElem) {
            url = msg.videoElem.videoUrl;
            fileName = msg.videoElem.videoUUID + '.mp4';
        } else if (msg.fileElem) {
            url = msg.fileElem.sourceUrl;
            fileName = msg.fileElem.fileName;
        }

        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
        }
    }, []);

    const openLocation = useCallback((lat: number, lng: number) => {
        window.open(`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=15/${lat}/${lng}`, '_blank');
    }, []);

    // GIF functions
    const loadFeaturedGifs = async () => {
        setGifLoading(true);
        try {
            const response = await getFeaturedGifs(40);
            setGifs(response.items);
        } catch (error) {
            console.error('Error loading featured GIFs:', error);
        } finally {
            setGifLoading(false);
        }
    };

    const handleGifSearch = async () => {
        setGifLoading(true);
        try {
            const response = await searchGifs(gifSearchQuery, 20);
            setGifs(response.items);
        } catch (error) {
            console.error('Error searching GIFs:', error);
        } finally {
            setGifLoading(false);
        }
    };

    const handleGifSelect = (gif: KlipyGif) => {
        // Toggle selection
        setSelectedStickers(prev => {
            const isSelected = prev.some(s => s.slug === gif.slug);
            if (isSelected) {
                return prev.filter(s => s.slug !== gif.slug);
            } else {
                return [...prev, gif];
            }
        });
    };

    const handleSendStickers = async () => {
        if (!selectedRoom?.groupID || selectedStickers.length === 0) return;

        // Register share for analytics for all selected stickers
        for (const gif of selectedStickers) {
            await registerShare(gif.slug);
        }

        // Combine all sticker URLs with | separator (Flutter format)
        const stickerUrls = selectedStickers.map(gif => gif.gif.url);
        const combinedText = `[STICKERS:${stickerUrls.join('|')}]`;

        const requestId = createUniqueId();

        // Send as a single combined message
        const sentMessage = await sendTextMessage({
            text: combinedText,
            groupID: selectedRoom.groupID,
            requestId
        });

        // Add sent message to state
        if (sentMessage) {
            // Mark as processed to prevent duplicate from context
            processedMessageIdsRef.current.add(sentMessage.clientMsgID);

            // Force scroll to bottom for sent stickers
            isNearBottomRef.current = true;
            pendingScrollRef.current = 'bottom';

            setMessages(prev => {
                return upsertMessageByClientID(prev, sentMessage);
            });
        }

        // Reset and close
        setSelectedStickers([]);
        setShowGifPicker(false);
        setGifSearchQuery('');
    };

    // Typing indicator - use conversationID for accurate status tracking
    const currentTyping = selectedRoom?.conversationID ? typingStatus[selectedRoom.conversationID] : null;
    const isOtherTyping = !!(currentTyping?.userID && currentTyping.userID !== currentUserID && (currentTyping.platformIDs?.length ?? 0) > 0);
    const typingMember = isOtherTyping
        ? selectedRoom?.members?.find(m => m.userID === currentTyping?.userID)
        : null;
    // Decode hex-encoded userID back to email as last resort
    const decodeHexId = (hexId: string | undefined): string => {
        if (!hexId) return '';
        try {
            return hexId.match(/.{1,2}/g)?.map(h => String.fromCharCode(parseInt(h, 16))).join('') || hexId;
        } catch { return hexId; }
    };
    const typingUserName = isOtherTyping
        ? typingMember?.nickname || typingMember?.email || decodeHexId(currentTyping?.userID)
        : null;
    const typingUserEmail = isOtherTyping
        ? typingMember?.email || ''
        : '';

    // Diagnostic logs for typing
    if (currentTyping) {
        console.log('CenterPanel Typing Check:', {
            conversationID: selectedRoom?.conversationID,
            currentTyping,
            currentUserID,
            isOtherTyping,
            typingUserName,
            memberCount: selectedRoom?.members?.length
        });
    }

    // Track typing state changes and preserve scroll position when typing indicator changes
    useEffect(() => {
        const container = messagesContainerRef.current;

        if (previousIsOtherTypingRef.current !== isOtherTyping) {
            console.log(`isOtherTyping changed: ${previousIsOtherTypingRef.current} -> ${isOtherTyping}`);
            // If user is scrolled up, preserve scroll position when typing indicator changes
            if (!isNearBottomRef.current && container) {
                const currentScrollTop = container.scrollTop;
                requestAnimationFrame(() => {
                    if (container.scrollTop !== currentScrollTop) {
                        container.scrollTop = currentScrollTop;
                        ignoreScrollUntilRef.current = Date.now() + 200;
                    }
                });
            } else if (isNearBottomRef.current && container && isOtherTyping) {
                // If user is at bottom and someone starts typing, force scroll to bottom to ensure indicator is visible
                console.log('Forcing scroll to bottom for typing indicator');
                requestAnimationFrame(() => {
                    container.scrollTop = container.scrollHeight;
                });
            }
        }
        previousIsOtherTypingRef.current = isOtherTyping;
    }, [isOtherTyping]);

    // Check if can send
    const canSend = messageText.trim() || selectedFiles.length > 0 || recordedAudio || selectedLocation;

    // Render helpers
    const getSenderEmail = (sendID: string) => {
        return selectedRoom?.members?.find(m => m.userID === sendID)?.email || '';
    };

    if (!selectedRoom) {
        return (
            <div className={styles.centerPanel}>
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>💬</div>
                    <h3>{t.selectConversation}</h3>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.centerPanel}>
            {/* Header */}
            <div className={`${styles.chatHeaderWrapper} ${hideHeader ? styles.headerHidden : ''}`}>
                {!hideHeader && (
                <div className={styles.chatHeader}>
                    <div className={styles.roomInfo}>
                        <div className={styles.roomAvatar} style={{ backgroundColor: selectedRoom.bgColor }}>
                            {selectedRoom.symbol}
                        </div>
                        <div className={styles.roomDetails}>
                            <h3>{getRoomDisplayName(selectedRoom.groupName, selectedRoom.customerNo, 37)}</h3>
                            <p>{selectedRoom.emails}</p>
                        </div>
                    </div>
                    <div className={styles.chatActions}>
                        {showSearchIcon && (
                            <button
                                className={`${styles.actionBtn} ${isSearchExpanded ? styles.active : ''}`}
                                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                                title={t.searchMessages}
                            >
                                <Search size={18} />
                            </button>
                        )}
                        <button className={styles.actionBtn} onClick={() => setShowMembersPopup(true)} title={t.viewMembers}>
                            <Users size={18} />
                        </button>
                    </div>
                </div>
                )}

                {/* Expandable Search Panel */}
                <div className={`${styles.searchPanel} ${isSearchExpanded ? styles.expanded : ''}`}>
                    <div className={styles.searchInputWrapper}>
                        <Search size={16} className={styles.searchInputIcon} />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t.searchMessages}
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            className={styles.searchInput}
                        />
                        {searchInputValue && (
                            <button className={styles.clearSearchBtn} onClick={clearSearch}>
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {searchResults.length > 0 && (
                        <div className={styles.searchResults}>
                            {searchResults.map((result) => (
                                <div
                                    key={result.messageId}
                                    className={styles.searchResultItem}
                                    onClick={() => jumpToMessage(result.messageId)}
                                >
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultSender}>{result.senderNickname}</span>
                                        <p className={styles.resultSnippet}
                                            dangerouslySetInnerHTML={{ __html: result.snippet }} />
                                    </div>
                                    <span className={styles.resultTime}>
                                        {new Date(result.timestamp).toLocaleDateString(getLocale(language))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {isSearchLoading && <div className={styles.searchLoading}>{t.searching}</div>}
                    {searchInputValue && !isSearchLoading && searchResults.length === 0 && (
                        <div className={styles.noResults}>{t.noSearchResults}</div>
                    )}
                </div>
            </div>

            {/* Messages Area Wrapper - for positioning the floating button */}
            <div className={styles.messagesWrapper}>
                {/* Messages */}
                <div
                    className={styles.chatMessages}
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                >
                    {isLoadingMore && (
                        <div className={styles.loadingIndicator}>{t.loadingMore}</div>
                    )}

                    {isLoading ? (
                        <div className={styles.loadingIndicator}>{t.loading}</div>
                    ) : groupedMessages.length === 0 ? (
                        <div className={styles.emptyMessages}>
                            <div className={styles.emptyMessagesIcon}>💬</div>
                            <p>{t.noMessages}</p>
                        </div>
                    ) : (() => {
                        // Find the last outgoing group to show "Read on" timestamp
                        let lastOutgoingGroupIndex = -1;
                        for (let i = groupedMessages.length - 1; i >= 0; i--) {
                            if (groupedMessages[i].sendID === currentUserID) {
                                lastOutgoingGroupIndex = i;
                                break;
                            }
                        }

                        return groupedMessages.map((group, groupIndex) => {
                            const isOutgoing = group.sendID === currentUserID;
                            const senderEmail = getSenderEmail(group.sendID);
                            const firstItem = group.items[0];
                            const senderNickname = firstItem.senderNickname || group.sendID;
                            const senderAvatar = firstItem.senderFaceUrl || '';

                            // Check if the entire group has any displayable content
                            const groupHasDisplayableContent = group.items.some(hasDisplayableContent);

                            // Skip rendering groups with no displayable content.
                            if (!groupHasDisplayableContent) {
                                return null;
                            }

                            const groupKey = group.items[0]?.clientMsgID ?? `${group.sendID}-${group.sendTime}-${groupIndex}`;

                            return (
                                <div
                                    key={`group-${groupKey}`}
                                    className={`${styles.message} ${isOutgoing ? styles.outgoing : styles.incoming} ${group.items.length > 1 ? styles.group : ''} ${highlightedMessageId === firstItem.clientMsgID ? styles.highlighted : ''}`}
                                    onContextMenu={(e) => handleContextMenu(e, group)}
                                    data-message-id={firstItem.clientMsgID}
                                >
                                    {/* Avatar */}
                                    {!isOutgoing && (
                                        <div className={styles.messageAvatar}>
                                            {senderAvatar ? (
                                                <img src={senderAvatar} alt={senderNickname} />
                                            ) : (
                                                <div className={styles.avatarInitials} style={{ backgroundColor: getAvatarColor(senderNickname) }}>
                                                    {getInitials(senderEmail || senderNickname)}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Message Content */}
                                    <div className={styles.messageContent}>
                                        {/* Meta info - Only show sender email for incoming messages */}
                                        {!isOutgoing && (
                                            <div className={`${styles.messageMeta} ${styles.incoming}`}>
                                                <span className={styles.senderEmail}>{senderEmail}</span>
                                            </div>
                                        )}

                                        {/* Render message types */}
                                        {group.albumID && group.albumImages && group.albumImages.length > 0 ? (
                                            (() => {
                                                const albumStatusMessage = getAlbumStatusMessage(group.albumImages);
                                                const albumReadAt = getLatestReadAt(group.albumImages) || group.readAt;
                                                const albumReadTimestamp = groupIndex === lastOutgoingGroupIndex
                                                    ? (albumReadAt || albumStatusMessage.readAt)
                                                    : undefined;
                                                const albumUrls = group.albumImages
                                                    .map(m => m.pictureElem?.sourcePicture?.url || m.pictureElem?.snapshotPicture?.url || m.pictureElem?.bigPicture?.url || '')
                                                    .filter(url => url);
                                                const albumText = `[ALBUM:${albumUrls.join('|')}]`;
                                                return (
                                                    <>
                                                        {albumUrls.length > 0 ? (
                                                            <AlbumMessage
                                                                albumText={albumText}
                                                                onOpenViewer={openImageViewer}
                                                            />
                                                        ) : albumStatusMessage.status === MessageStatus.Failed ? (
                                                            <div className={styles.messageText}>
                                                                {albumStatusMessage.sendError || t.photoUploadFailed}
                                                            </div>
                                                        ) : null}
                                                        <MessageStatusMeta
                                                            msg={{
                                                                ...albumStatusMessage,
                                                                readAt: albumStatusMessage.readAt || albumReadAt,
                                                            }}
                                                            isOutgoing={isOutgoing}
                                                            groupReadAt={albumReadAt}
                                                            readTimestamp={albumReadTimestamp}
                                                        />
                                                    </>
                                                );
                                            })()
                                        ) : (
                                            group.items.map((msg, msgIndex) => {
                                                const reactionData = messageReactions[msg.clientMsgID];
                                                const reactionSignature = getMessageReactionSignature(reactionData);

                                                return (
                                                    <MessageItem
                                                        key={msg.clientMsgID}
                                                        msg={msg}
                                                        members={selectedRoom?.members || []}
                                                        isLastInGroup={msgIndex === group.items.length - 1}
                                                        isOutgoing={isOutgoing}
                                                        currentUserID={currentUserID}
                                                        groupReadAt={group.readAt}
                                                        readTimestamp={
                                                            groupIndex === lastOutgoingGroupIndex && msgIndex === group.items.length - 1
                                                                ? (msg.readAt || group.items[group.items.length - 1]?.readAt)
                                                                : undefined
                                                        }
                                                        reactions={reactionData}
                                                        reactionSignature={reactionSignature}
                                                        groupID={selectedRoom?.groupID || ''}
                                                        onImageClick={viewImage}
                                                        openAlbumViewer={openImageViewer}
                                                        openVideoViewer={viewVideo}
                                                        downloadFile={downloadFile}
                                                        openLocation={openLocation}
                                                        sendReaction={sendReaction}
                                                        onMentionClick={handleMentionClick}
                                                    />
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    })()}
                    {/* Typing indicator */}
                    {isOtherTyping && typingUserName && (
                        <div className={styles.typingIndicator}>
                            <div className={styles.typingAvatar}>
                                <div className={styles.avatarInitials} style={{ backgroundColor: getAvatarColor(typingUserName) }}>
                                    {getInitials(typingUserEmail || typingUserName || '')}
                                </div>
                            </div>
                            <div className={styles.typingBubble}>
                                <span className={styles.typingDot}></span>
                                <span className={styles.typingDot}></span>
                                <span className={styles.typingDot}></span>
                            </div>
                            <span className={styles.typingText}>{typingUserName} {t.isTyping}</span>
                        </div>
                    )}

                </div>

                {/* New messages floating button - like WhatsApp, Telegram, Slack */}
                {showNewMessageButton && (
                    <button
                        className={styles.newMessageButton}
                        onClick={scrollToBottomAndClear}
                        aria-label={`${unreadCount} ${unreadCount > 1 ? t.newMessages : t.newMessage}`}
                    >
                        <ChevronDown size={20} />
                        {unreadCount > 0 && (
                            <span className={styles.unreadBadge}>
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                )}
            </div>

            {/* Input Area */}
            <div className={styles.chatInput}>
                {/* Reply preview */}
                {replyingTo && (() => {
                    const preview = getQuotedContentPreview(replyingTo, t);
                    const hasMediaPreview = preview.type === 'image' || preview.type === 'video' || preview.type === 'sticker';

                    // Get thumbnail URL for media types
                    let thumbnailUrl: string | undefined;
                    if (preview.type === 'image' && replyingTo.pictureElem) {
                        thumbnailUrl = replyingTo.pictureElem.snapshotPicture?.url || replyingTo.pictureElem.sourcePicture?.url;
                    } else if (preview.type === 'video' && replyingTo.videoElem) {
                        thumbnailUrl = replyingTo.videoElem.snapshotUrl;
                    } else if (preview.type === 'sticker' && replyingTo.textElem?.content) {
                        const urlMatch = replyingTo.textElem.content.match(/https?:\/\/[^\s]+/);
                        if (urlMatch) thumbnailUrl = urlMatch[0];
                    }

                    return (
                        <div className={styles.replyPreview}>
                            <div className={`${styles.replyPreviewContent} ${hasMediaPreview ? styles.replyWithMedia : ''}`}>
                                <div className={styles.replyIndicator}></div>
                                <div className={styles.replyInfo}>
                                    <span className={styles.replySender}>{replyingTo.senderNickname}</span>
                                    <span className={styles.replyText}>{preview.text}</span>
                                </div>
                                {thumbnailUrl && (
                                    <div className={styles.replyThumbnail}>
                                        <img src={thumbnailUrl} alt="" />
                                        {preview.type === 'video' && (
                                            <div className={styles.replyVideoIcon}>
                                                <Play size={10} />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button className={styles.replyCancelBtn} onClick={() => setReplyingTo(null)}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                    );
                })()}

                {fileUploadError && (
                    <div className={styles.fileUploadError} role="alert">
                        {fileUploadError}
                    </div>
                )}

                {/* Selected files preview */}
                {selectedFiles.length > 0 && (
                    <div className={styles.selectedFiles}>
                        <div className={styles.selectedFilesHeader}>
                            <span>{selectedFiles.length} {t.filesSelected}</span>
                            <button
                                className={styles.clearFilesBtn}
                                onClick={() => {
                                    setSelectedFiles([]);
                                    setFileUploadError('');
                                }}
                            >
                                {t.clearAll}
                            </button>
                        </div>
                        <div className={styles.filePreviewList}>
                            {selectedFiles.map((file, i) => (
                                <div key={i} className={styles.filePreviewItem}>
                                    <div className={styles.filePreviewContent}>
                                        {file.preview ? (
                                            <div className={styles.filePreviewWrapper}>
                                                <img src={file.preview} alt={file.name} className={styles.filePreviewImage} style={{ opacity: file.isUploading ? 0.5 : 1 }} />
                                                {file.isUploading && (
                                                    <div className={styles.uploadingOverlay}>
                                                        <div className={styles.spinner}></div>
                                                        <span className={styles.uploadingText}>{t.uploading}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className={styles.filePreviewDocument} style={{ opacity: file.isUploading ? 0.5 : 1 }}>
                                                <FileText size={16} />
                                                {file.isUploading && (
                                                    <div className={styles.uploadingOverlay}>
                                                        <div className={styles.spinner}></div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className={styles.fileInfo}>
                                            <span className={styles.fileName}>{file.name}</span>
                                            <span className={styles.fileSize}>
                                                {formatFileSize(file.size)}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className={styles.removeFileBtn}
                                        onClick={() => removeFile(i)}
                                        disabled={file.isUploading}
                                        style={{ opacity: file.isUploading ? 0.5 : 1 }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Location preview */}
                {selectedLocation && (
                    <div className={styles.selectedLocation}>
                        <div className={styles.locationPreview} style={{ opacity: isLoading ? 0.7 : 1 }}>
                            <MapPin size={20} className={styles.locationIcon} />
                            <div className={styles.locationInfo}>
                                <span className={styles.locationLabel}>
                                    {isLoading ? t.sendingLocation : t.locationReadyToSend}
                                </span>
                                <span className={styles.locationCoords}>{selectedLocation.description}</span>
                            </div>
                            {isLoading ? (
                                <div className={styles.spinner}></div>
                            ) : (
                                <button className={styles.removeLocationBtn} onClick={() => setSelectedLocation(null)}>
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* User suggestions */}
                {showUserSuggestions && (
                    <div className={styles.userSuggestions}>
                        {filteredUsers.map(user => (
                            <div key={user.userID} className={styles.suggestionItem} onClick={() => selectUser(user)}>
                                <div className={styles.suggestionAvatar} style={{ backgroundColor: getAvatarColor(user.nickname || user.userID) }}>
                                    {getInitials(user.nickname || user.userID)}
                                </div>
                                <div className={styles.suggestionInfo}>
                                    <span className={styles.suggestionName}>{user.nickname || user.userID}</span>
                                    <span className={styles.suggestionEmail}>{user.email}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Emoji picker */}
                {showEmojiPicker && (
                    <div className={styles.emojiPickerWrapper} ref={emojiPickerRef}>
                        <EmojiPicker
                            onEmojiClick={(emojiData) => {
                                setMessageText(prev => prev + emojiData.emoji);
                            }}
                            autoFocusSearch={false}
                            width="100%"
                            height="400px"
                            previewConfig={{ showPreview: false }}
                            searchPlaceHolder={t.searchMessages}
                        />
                    </div>
                )}

                {/* Stickers UI temporarily hidden. Set UIConfig.STICKERS_UI_ENABLED to true to restore. */}
                {UIConfig.STICKERS_UI_ENABLED && showGifPicker && (
                    <div className={styles.gifPicker} ref={gifPickerRef}>
                        <div className={styles.gifPickerHeader}>
                            <span className={styles.gifPickerTitle}>
                                {t.stickers} {selectedStickers.length > 0 && `(${selectedStickers.length})`}
                            </span>
                            <button
                                className={styles.gifPickerClose}
                                onClick={() => {
                                    setShowGifPicker(false);
                                    setSelectedStickers([]);
                                }}
                                aria-label={t.close}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className={styles.gifSearchContainer}>
                            <Search size={16} className={styles.gifSearchIcon} />
                            <input
                                type="text"
                                placeholder={t.searchStickers}
                                value={gifSearchQuery}
                                onChange={(e) => setGifSearchQuery(e.target.value)}
                                className={styles.gifSearchInput}
                            />
                        </div>
                        <div className={styles.gifGrid}>
                            {gifLoading ? (
                                <div className={styles.gifLoading}>{t.loading}</div>
                            ) : (
                                gifs.map((gif) => {
                                    const isSelected = selectedStickers.some(s => s.slug === gif.slug);
                                    return (
                                        <button
                                            key={gif.slug}
                                            onClick={() => handleGifSelect(gif)}
                                            className={`${styles.gifItem} ${isSelected ? styles.gifItemSelected : ''}`}
                                            title={gif.title}
                                        >
                                            <img
                                                src={gif.preview || gif.gif.url}
                                                alt={gif.title}
                                                loading="lazy"
                                            />
                                            {isSelected && (
                                                <div className={styles.gifSelectedBadge}>✓</div>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                        {selectedStickers.length > 0 && (
                            <div className={styles.gifSendContainer}>
                                <button
                                    onClick={handleSendStickers}
                                    className={styles.gifSendButton}
                                >
                                    {selectedStickers.length > 1 ? t.sendStickers : t.sendSticker}
                                </button>
                            </div>
                        )}
                        <div className={styles.gifAttribution}>
                            {t.poweredByKlipy}
                        </div>
                    </div>
                )}

                {/* Input container */}
                <div className={styles.inputContainer}>
                    {/* Recording panel */}
                    {(isRecording || recordedAudio) && (
                        <div className={styles.recordingPanel}>
                            <div className={styles.recordingContent}>
                                <div className={styles.recordingWave}>
                                    <span className={styles.waveBar}></span>
                                    <span className={styles.waveBar}></span>
                                    <span className={styles.waveBar}></span>
                                    <span className={styles.waveBar}></span>
                                    <span className={styles.waveBar}></span>
                                </div>
                                <div className={styles.voiceIcon}>
                                    <Mic size={16} />
                                </div>
                                <div className={styles.recordingInfo}>
                                    <span className={styles.recordingLabel}>
                                        {isRecording ? t.recording : (isLoading ? t.uploadingVoice : t.voiceMessageReady)}
                                    </span>
                                    <span className={styles.recordingDuration}>
                                        {formatDuration(recordedAudio?.duration || recordingDuration)}
                                    </span>
                                </div>
                                {recordedAudio && !isLoading && (
                                    <button className={styles.audioPlayBtn} onClick={toggleAudioPlayback}>
                                        {isAudioPlaying ? <Pause size={16} /> : <Play size={16} />}
                                    </button>
                                )}
                                {isLoading && (
                                    <div className={styles.spinner}></div>
                                )}
                                <div className={styles.recordingActions}>
                                    <button
                                        className={`${styles.recordingActionBtn} ${styles.deleteBtn}`}
                                        onClick={deleteRecording}
                                        disabled={isLoading}
                                        style={{ opacity: isLoading ? 0.5 : 1 }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    {isRecording && (
                                        <button className={`${styles.recordingActionBtn} ${styles.stopBtn}`} onClick={stopRecording}>
                                            <Square size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Attachment menu button */}
                    <div className={styles.attachmentMenuWrapper}>
                        <button
                            className={`${styles.attachmentMenuBtn} ${showAttachmentMenu ? styles.active : ''}`}
                            onClick={(e) => { e.stopPropagation(); setShowAttachmentMenu(!showAttachmentMenu); }}
                            disabled={isRecording}
                            title={t.send}
                        >
                            <Plus size={20} />
                        </button>

                        {/* Attachment menu popup */}
                        {showAttachmentMenu && (
                            <div className={styles.attachmentMenu} ref={attachmentMenuRef}>
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); imageInputRef.current?.click(); setShowAttachmentMenu(false); }}
                                    title={t.image}
                                >
                                    <Image size={20} />
                                    <span>{t.image}</span>
                                </button>
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); videoInputRef.current?.click(); setShowAttachmentMenu(false); }}
                                    title={t.video}
                                >
                                    <Video size={20} />
                                    <span>{t.video}</span>
                                </button>
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); setShowAttachmentMenu(false); }}
                                    title={t.file}
                                >
                                    <Paperclip size={20} />
                                    <span>{t.file}</span>
                                </button>
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(!showEmojiPicker); setShowAttachmentMenu(false); }}
                                    title={t.emoji}
                                >
                                    <Smile size={20} />
                                    <span>{t.emoji}</span>
                                </button>
                                {UIConfig.STICKERS_UI_ENABLED && (
                                    <button
                                        className={styles.attachmentMenuItem}
                                        onClick={(e) => { e.stopPropagation(); setShowGifPicker(!showGifPicker); setShowAttachmentMenu(false); }}
                                        title={t.stickers}
                                    >
                                        <Search size={20} />
                                        <span>{t.stickers}</span>
                                    </button>
                                )}
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); toggleRecording(); setShowAttachmentMenu(false); }}
                                    disabled={!!recordedAudio}
                                    title={t.voice}
                                >
                                    <Mic size={20} />
                                    <span>{t.voice}</span>
                                </button>
                                <button
                                    className={styles.attachmentMenuItem}
                                    onClick={(e) => { e.stopPropagation(); getLocation(); setShowAttachmentMenu(false); }}
                                    disabled={isGettingLocation}
                                    title={t.location}
                                >
                                    <MapPin size={20} />
                                    <span>{t.location}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Text input */}
                    <textarea
                        ref={textareaRef}
                        className={`${styles.messageInput} ${isRecording || recordedAudio ? styles.hiddenInput : ''}`}
                        placeholder={t.typeMessage}
                        value={messageText}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        rows={1}
                    />

                    {/* Send button */}
                    <button
                        className={styles.sendBtn}
                        onClick={handleSendMessage}
                        disabled={!canSend || isLoading || selectedFiles.some(f => f.isUploading)}
                    >
                        {(isLoading || selectedFiles.some(f => f.isUploading)) ? (
                            <div className={styles.spinner}></div>
                        ) : (
                            <Send size={18} />
                        )}
                    </button>

                    {/* Hidden inputs */}
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) => handleFileSelect(e, 'image')}
                    />
                    <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        hidden
                        onChange={(e) => handleFileSelect(e, 'video')}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => handleFileSelect(e, 'document')}
                    />
                    {audioPreviewUrl && <audio ref={audioPreviewRef} src={audioPreviewUrl} hidden />}
                </div>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <div
                    ref={contextMenuRef}
                    className={styles.contextMenuContainer}
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Quick Reactions Row - Scrollable */}
                    <div className={styles.quickReactions}>
                        {REACTION_EMOJIS.map((emoji) => (
                            <button
                                key={emoji}
                                className={styles.quickReactionBtn}
                                onClick={() => handleReaction(emoji)}
                                title={emoji}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                    <ul className={styles.contextMenuList}>
                        <li onClick={handleReply}>
                            <Reply size={18} />
                            <span>{t.reply}</span>
                        </li>
                        {/* Delete for me - always available for any message */}
                        {/* <li onClick={handleDeleteForMe}>
                            <Trash2 size={18} />
                            <span>{t.deleteForMe}</span>
                        </li> */}
                        {/* Unsend/Revoke - only for sender's own messages within 2 minutes */}
                        {contextMenu.message.sendID === currentUserID && (() => {
                            const firstMsg = contextMenu.message.items[0];
                            const messageAge = Date.now() - firstMsg.sendTime;
                            return messageAge <= MessageConfig.REVOKE_TIME_LIMIT_MS;
                        })() && (
                                <li onClick={handleRevoke}>
                                    <X size={18} />
                                    <span>{t.unsend}</span>
                                </li>
                            )}
                    </ul>
                </div>
            )}

            {/* Full Reaction Picker */}
            {contextMenu && showReactionPicker && (
                <div
                    ref={reactionPickerRef}
                    className={styles.reactionPickerOverlay}
                    onClick={() => setShowReactionPicker(false)}
                >
                    <div
                        className={styles.reactionPicker}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.reactionPickerHeader}>
                            <span>{t.selectReaction}</span>
                            <button
                                className={styles.reactionPickerClose}
                                onClick={() => setShowReactionPicker(false)}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className={styles.reactionGrid}>
                            {REACTION_EMOJIS.map((emoji) => (
                                <button
                                    key={emoji}
                                    className={styles.reactionGridItem}
                                    onClick={() => {
                                        handleReaction(emoji);
                                        setShowReactionPicker(false);
                                    }}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Members Popup */}
            {showMembersPopup && (
                <div className={`${styles.popupOverlay} ${styles.show}`} onClick={() => setShowMembersPopup(false)}>
                    <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closePopupBtn} onClick={() => setShowMembersPopup(false)}>&times;</button>
                        <div className={styles.popupHeader}>
                            <h3 className={styles.popupTitle}>{t.members} ({selectedRoom.members?.length || 0}) ver {packageJson.version}</h3>
                        </div>
                        <div className={styles.membersList}>
                            {selectedRoom.members?.map(member => (
                                <div
                                    key={member.userID}
                                    className={styles.memberItem}
                                    onClick={() => setSelectedMember(member)}
                                >
                                    <div className={styles.memberAvatar}>
                                        {member.faceURL ? (
                                            <img src={member.faceURL} alt={member.nickname} />
                                        ) : (
                                            <div className={styles.avatarInitials} style={{ backgroundColor: getAvatarColor(member.nickname || member.userID) }}>
                                                {getInitials(member.nickname || member.userID)}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.memberInfo}>
                                        <span className={styles.memberEmail}>{member.email || member.userID}</span>
                                        <span className={styles.memberName}>{member.nickname || member.userID}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Member Detail Popup */}
            {selectedMember && (
                <div className={`${styles.popupOverlay} ${styles.show}`} onClick={() => setSelectedMember(null)}>
                    <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closePopupBtn} onClick={() => setSelectedMember(null)}>&times;</button>
                        {selectedMember.faceURL ? (
                            <img src={selectedMember.faceURL} alt={selectedMember.nickname} className={styles.popupAvatar} />
                        ) : (
                            <div
                                className={styles.popupAvatarInitials}
                                style={{ backgroundColor: getAvatarColor(selectedMember.nickname || selectedMember.userID) }}
                            >
                                {getInitials(selectedMember.nickname || selectedMember.userID)}
                            </div>
                        )}
                        <h3 className={styles.popupName}>{selectedMember.nickname || selectedMember.userID}</h3>
                        <div className={styles.popupContactInfo}>
                            {selectedMember.phone && (
                                <div className={styles.popupContactItem}>
                                    <div className={styles.popupContactIcon}><Phone size={18} /></div>
                                    <span>{selectedMember.phone}</span>
                                </div>
                            )}
                            {selectedMember.email && (
                                <div className={styles.popupContactItem}>
                                    <div className={styles.popupContactIcon}><Mail size={18} /></div>
                                    <span>{selectedMember.email}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Album Sending Loading Overlay */}
            {isSendingAlbum && albumProgress && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingSpinner}></div>
                        <p className={styles.loadingText}>
                            {t.sendingAlbum} {albumProgress.current}/{albumProgress.total}
                        </p>
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${(albumProgress.current / albumProgress.total) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default CenterPanel;
